import { DEFAULT_REFERENCE_QUESTIONS } from "@shared/constants/reference-check-template";
import { formatCriterionScore } from "@shared/custom-feedback-format";
import type {
  Applicant,
  Application,
  ApplicationHistory,
  CustomCriterionScore,
  Interview,
  InterviewStage,
  JobOpening,
  Offer,
  PropertyDefinition,
  PropertyValue,
  ReferenceCheck,
} from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRightLeft,
  Check,
  CheckCheck,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ClipboardEdit,
  Clock,
  Copy,
  DollarSign,
  ExternalLink,
  Eye,
  FileText,
  FlaskConical,
  ImagePlus,
  Info,
  Linkedin,
  Loader2,
  Mail,
  MessageSquare,
  MoreHorizontal,
  MoreVertical,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Send,
  StickyNote,
  Trash2,
  UserCog,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import {
  CandidateExcitementHeaderBlock,
  CandidateExcitementInterviewBlock,
} from "@/components/candidate-excitement-display";
import { ChangeInterviewerDialog } from "@/components/change-interviewer-dialog";
import { CreateOfferDialog } from "@/components/create-offer-dialog";
import { EditApplicantDialog } from "@/components/edit-applicant-dialog";
import type { InterviewFeedbackData } from "@/components/interview-feedback-dialog";
import { InterviewFeedbackDialog } from "@/components/interview-feedback-dialog";
import { MoveConfirmationDialog } from "@/components/move-confirmation-dialog";
import { PropertyValueCell } from "@/components/property-value-cell";
import { RescheduleConfirmDialog } from "@/components/reschedule-confirm-dialog";
import { ScheduleInterviewDialog } from "@/components/schedule-interview-dialog";
import { SendInterviewEmailDialog } from "@/components/send-interview-email-dialog";
import { SendRejectionEmailDialog } from "@/components/send-rejection-email-dialog";
import { StatusBadge } from "@/components/status-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { useMoveApplication } from "@/hooks/use-move-application";
import { useRescheduleInterview } from "@/hooks/use-reschedule-interview";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { formatCurrency, formatDate, formatSharesRange } from "@/lib/constants";
import type { InterviewContextLabel } from "@/lib/interview-utils";
import { interviewStatusColors } from "@/lib/interview-utils";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { cn, ensureExternalUrl } from "@/lib/utils";

type GroupFeedback = {
  id: string;
  employeeId: string;
  employeeName: string;
  gcaVerdict: string | null;
  gcaNotes: string | null;
  roleKnowledgeVerdict: string | null;
  roleKnowledgeNotes: string | null;
  compWayVerdict: string | null;
  compWayNotes: string | null;
  customCriteriaScores?: CustomCriterionScore[] | null;
  candidateExcitementScore: number | null;
  candidateExcitementNote: string | null;
  contextSignals: string | null;
  generalComments: string | null;
  notesForNextInterviewer: string | null;
  recommendation: string | null;
  createdAt: string | null;
};

/** Renders the scores of a stage's custom feedback form (criterion + score
 * snapshot), in the same visual language as the default GCA/RK/CW blocks. */
function CustomCriteriaDisplay({ scores }: { scores: CustomCriterionScore[] }) {
  return (
    <>
      {scores.map((s) => (
        <div key={s.criterionId}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-muted-foreground">{s.label}</p>
            <Badge
              variant="outline"
              className="text-[10px] border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400"
            >
              {formatCriterionScore(s)}
            </Badge>
          </div>
          {s.notes && (
            <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-2.5 py-2 whitespace-pre-wrap">
              {s.notes}
            </p>
          )}
        </div>
      ))}
    </>
  );
}

type InterviewWithDetails = Interview & {
  interviewer: { id: string; fullName: string; schedulingLink: string | null } | null;
  groupMemberNames?: string[] | null;
  groupMemberIds?: string[] | null;
  feedbacks?: GroupFeedback[];
  stage: { id: string; stageName: string; stageOrder: number; materials: string | null } | null;
  feedbackRedacted?: boolean;
  contextStatus?: string | null;
};

type OfferWithDetails = Offer & {
  rejector?: { id: string; fullName: string } | null;
  creator?: { id: string; fullName: string } | null;
};

type ApplicationDetails = Application & {
  applicant?: Applicant;
  jobOpening?: JobOpening;
  interviews: InterviewWithDetails[];
  history: ApplicationHistory[];
  interviewStages: InterviewStage[];
  offers?: OfferWithDetails[];
  dossierDecidedByEmployee?: { id: string; fullName: string } | null;
  referenceChecks?: ReferenceCheck[];
  isHiringManager: boolean;
  /** Unified ATS management capability (admin OR recruiting OR hiring manager). */
  canManage: boolean;
};

type ApplicationNoteWithAuthor = {
  id: string;
  applicationId: string;
  authorId: string;
  content: string;
  createdAt: string;
  type: "free" | "interview_reply" | "interview_note" | "interview_note_reply";
  interviewId?: string | null;
  parentId?: string | null;
  attachments?: string[] | null;
  author?: { id: string; fullName: string };
};

const NUMBERED_LINE_RE = /^\d+([.)]\s|\s*-\s)/;
const NUMBERED_PREFIX_RE = /^\d+([.)]\s+|\s*-\s+)/;

function matchesQuestion(text: string, question: string): boolean {
  const words = question
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 3);
  const t = text.toLowerCase();
  const matches = words.filter((w) => t.includes(w)).length;
  return matches >= Math.max(2, Math.floor(words.length * 0.5));
}

function parseReferenceCheckResponses(
  text: string,
  questions: string[],
): { question: string; answer: string }[] {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  // Detect enumerated format: at least 2 lines match numbered prefix patterns (1. / 1) / 1 -)
  const numberedLines = lines.filter((l) => NUMBERED_LINE_RE.test(l));
  const isEnumerated = numberedLines.length >= 2;

  if (isEnumerated) {
    // Split into blocks: each block starts at a numbered line
    type Block = { header: string; rest: string[] };
    const blocks: Block[] = [];
    let current: Block | null = null;
    for (const line of lines) {
      if (NUMBERED_LINE_RE.test(line)) {
        if (current) blocks.push(current);
        current = { header: line.replace(NUMBERED_PREFIX_RE, ""), rest: [] };
      } else if (current) {
        current.rest.push(line);
      }
    }
    if (current) blocks.push(current);

    // For each block: if header matches a question text → answer = rest lines
    // Otherwise → answer = header + rest (plain answer list)
    return questions.map((q, i) => {
      const block = blocks[i];
      if (!block) return { question: q, answer: "" };
      if (matchesQuestion(block.header, q)) {
        return { question: q, answer: block.rest.join(" ").trim() };
      }
      const full = [block.header, ...block.rest].join(" ").trim();
      return { question: q, answer: full };
    });
  }

  // Q+A format: find lines matching a question, capture following lines as answer
  const result = questions.map((q) => ({ question: q, answer: "" }));

  const questionLineIndex: Map<number, number> = new Map();
  for (let qi = 0; qi < questions.length; qi++) {
    for (let li = 0; li < lines.length; li++) {
      if (matchesQuestion(lines[li], questions[qi])) {
        questionLineIndex.set(qi, li);
        break;
      }
    }
  }

  const sortedByLine = Array.from(questionLineIndex.entries()).sort((a, b) => a[1] - b[1]);
  for (let i = 0; i < sortedByLine.length; i++) {
    const [qi, li] = sortedByLine[i];
    const nextLine = sortedByLine[i + 1]?.[1] ?? lines.length;
    result[qi].answer = lines
      .slice(li + 1, nextLine)
      .join(" ")
      .trim();
  }

  return result;
}

interface ApplicantDetailModalProps {
  applicationId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyDefinitions: PropertyDefinition[];
  propertyValues: PropertyValue[];
}

function CopyToClipboard({
  text,
  toastMessage,
  icon,
  label,
}: {
  text: string;
  toastMessage: string;
  icon: React.ReactNode;
  label: React.ReactNode;
}) {
  const { toast } = useToast();
  return (
    <button
      type="button"
      className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors text-sm text-muted-foreground"
      onClick={() => {
        navigator.clipboard.writeText(text);
        toast({ title: toastMessage });
      }}
    >
      {icon}
      {label}
    </button>
  );
}

export function ApplicantDetailModal({
  applicationId,
  open,
  onOpenChange,
  propertyDefinitions,
  propertyValues,
}: ApplicantDetailModalProps) {
  const { toast } = useToast();
  const { employee } = useAuth();
  const [propertiesCollapsed, setPropertiesCollapsed] = useState(false);
  const [editingDefId, setEditingDefId] = useState<string | null>(null);
  const [deleteDefId, setDeleteDefId] = useState<string | null>(null);
  useEffect(() => {
    setPropertiesCollapsed(false);
    setEditingDefId(null);
  }, []);

  const [expandedInterviews, setExpandedInterviews] = useState<Set<string>>(new Set());
  // Interviews currently flashing a ring (when user arrives from the header
  // pill's "Ver leituras por etapa →" link). Auto-clears after a short timer.
  const [highlightedExcitementIds, setHighlightedExcitementIds] = useState<Set<string>>(new Set());
  const [newNoteContent, setNewNoteContent] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState("");
  const [replyingToInterviewId, setReplyingToInterviewId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState<Record<string, string>>({});
  const [newInterviewNoteContent, setNewInterviewNoteContent] = useState("");
  const [replyingToNoteId, setReplyingToNoteId] = useState<string | null>(null);
  const [noteReplyContent, setNoteReplyContent] = useState<Record<string, string>>({});
  const pendingAttachmentsRef = useRef<Record<string, string[]>>({});
  const [pendingAttachments, setPendingAttachmentsState] = useState<Record<string, string[]>>({});
  const setPendingAttachments = useCallback(
    (
      updater:
        | Record<string, string[]>
        | ((prev: Record<string, string[]>) => Record<string, string[]>),
    ) => {
      setPendingAttachmentsState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        pendingAttachmentsRef.current = next;
        return next;
      });
    },
    [],
  );
  const { uploadFile, isUploading: isUploadingFile } = useUpload();
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackDialogInterview, setFeedbackDialogInterview] =
    useState<InterviewFeedbackData | null>(null);
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isCreateOfferOpen, setIsCreateOfferOpen] = useState(false);
  const [deleteInterviewId, setDeleteInterviewId] = useState<string | null>(null);
  const [deleteConfirmCode, setDeleteConfirmCode] = useState("");
  const [deleteExpectedCode, setDeleteExpectedCode] = useState("");
  const [changeInterviewerForId, setChangeInterviewerForId] = useState<string | null>(null);

  const [isRejectionEmailDialogOpen, setIsRejectionEmailDialogOpen] = useState(false);
  const [sendEmailInterviewId, setSendEmailInterviewId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(["historico"]));
  const [sendConfirmOfferId, setSendConfirmOfferId] = useState<string | null>(null);
  const [deletingRcId, setDeletingRcId] = useState<string | null>(null);
  const [rcDrawerOpen, setRcDrawerOpen] = useState(false);
  const [rcTemplateOpen, setRcTemplateOpen] = useState(false);
  const [scheduleAnotherStageId, setScheduleAnotherStageId] = useState<string | null>(null);
  const [moveInterviewId, setMoveInterviewId] = useState<string | null>(null);
  const [moveTargetStageId, setMoveTargetStageId] = useState<string>("");
  const [deleteOfferId, setDeleteOfferId] = useState<string | null>(null);
  const [cancelOfferId, setCancelOfferId] = useState<string | null>(null);
  const [rejectManualOfferId, setRejectManualOfferId] = useState<string | null>(null);
  const [acceptOfferId, setAcceptOfferId] = useState<string | null>(null);
  const [acceptStartDate, setAcceptStartDate] = useState("");
  const [isSimulateOfferOpen, setIsSimulateOfferOpen] = useState(false);
  const [editingRc, setEditingRc] = useState<ReferenceCheck | null>(null);
  const [viewingRc, setViewingRc] = useState<ReferenceCheck | null>(null);
  const [rcPasteOpen, setRcPasteOpen] = useState(false);
  const [rcPasteText, setRcPasteText] = useState("");
  const [rcDrawerTab, setRcDrawerTab] = useState<"contato" | "respostas">("contato");
  const [rcContactForm, setRcContactForm] = useState({
    referrerName: "",
    referrerLinkedin: "",
    referrerPhone: "",
  });
  const [rcAnswersForm, setRcAnswersForm] = useState<{ question: string; answer: string }[]>([]);
  const scrollBodyRef = useRef<HTMLDivElement | null>(null);

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const scrollToSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.delete(sectionId);
      return next;
    });
    setTimeout(() => {
      const el = document.getElementById(`section-${sectionId}`);
      if (el && scrollBodyRef.current) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 50);
  };

  const toggleInterview = (id: string) => {
    setExpandedInterviews((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const {
    data: details,
    isError: isDetailsError,
    refetch: refetchDetails,
  } = useQuery({
    ...orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } }),
    enabled: !!applicationId && open,
    refetchInterval: (query: any) => {
      const sr = query.state.data?.aiScreeningResult as { status?: string } | null | undefined;
      return sr?.status === "pending" || sr?.status === "running" ? 5000 : false;
    },
  }) as unknown as {
    data: ApplicationDetails | undefined;
    isError: boolean;
    refetch: () => void;
  };

  // Unified ATS management capability (admin OR recruiting OR hiring manager),
  // computed by the backend. Single source for all edit/move gates in this modal.
  const canManage = details?.canManage === true;

  const { data: notes } = useQuery({
    ...orpc.applications.getNotes.queryOptions({ input: { id: applicationId! } }),
    enabled: !!applicationId && open,
  }) as unknown as { data: ApplicationNoteWithAuthor[] | undefined };

  // Candidate excitement scores — full series for this application, ordered by
  // stage_sequence (oldest first). Powers the header summary block (latest +
  // trajectory) and the per-interview block in the Entrevistas section.
  const { data: candidateExcitementSeries = [] } = useQuery({
    ...orpc.candidateExcitement.listByApplication.queryOptions({
      input: { applicationId: applicationId ?? "" },
    }),
    enabled: !!applicationId && open,
  });

  /**
   * Wired from the candidate excitement pill's popover ("Ver leituras por
   * etapa →"). Opens the Entrevistas section, expands every interview that
   * has a candidate excitement score, scrolls there, and briefly outlines
   * those interview cards so the user can spot where they landed.
   *
   * Inlines the scroll logic (rather than calling scrollToSection) because
   * the lint rule for exhaustive deps trips on scrollToSection's identity —
   * state setters and refs in scope are stable.
   */
  const handleShowExcitementHistory = useCallback(() => {
    const ids = candidateExcitementSeries.map((s) => s.interviewId);
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.delete("entrevistas");
      return next;
    });
    if (ids.length > 0) {
      setExpandedInterviews((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        return next;
      });
      setHighlightedExcitementIds(new Set(ids));
      window.setTimeout(() => setHighlightedExcitementIds(new Set()), 2500);
    }
    window.setTimeout(() => {
      const el = document.getElementById("section-entrevistas");
      if (el && scrollBodyRef.current) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 50);
  }, [candidateExcitementSeries]);

  const jobId = details?.jobOpening?.id;

  const updateDefMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; type?: string; showOnCard?: boolean };
    }) => {
      if (!jobId) throw new Error("Job opening ID not available");
      return rpcClient.jobOpenings.updatePropertyDefinition({ id: jobId, defId: id, ...data });
    },
    onSuccess: () => {
      if (jobId)
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({ input: { jobOpeningId: jobId } })
            .queryKey,
        });
    },
    onError: () => toast({ title: "Erro ao atualizar propriedade", variant: "destructive" }),
  });

  const deleteDefMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!jobId) throw new Error("Job opening ID not available");
      await rpcClient.jobOpenings.deletePropertyDefinition({ id: jobId, defId: id });
    },
    onSuccess: () => {
      if (jobId)
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({ input: { jobOpeningId: jobId } })
            .queryKey,
        });
    },
    onError: () => toast({ title: "Erro ao excluir propriedade", variant: "destructive" }),
  });

  const retriggerScreeningMutation = useMutation({
    mutationFn: async () => {
      if (!applicationId) throw new Error("No application ID");
      return rpcClient.applications.triggerAiScreening({ id: applicationId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      if (jobId) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({ input: { jobOpeningId: jobId } })
            .queryKey,
        });
      }
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const { data: movePeople } = useQuery({
    ...orpc.dashboard.people.queryOptions(),
    enabled: !!jobId,
  });

  const { data: moveStageInterviewers, isLoading: isLoadingMoveStageInterviewers } = useQuery({
    ...orpc.jobOpenings.getStageInterviewers.queryOptions({ input: { id: jobId! } }),
    enabled: !!jobId,
  });

  const moveApp = useMoveApplication({
    jobId,
    jobTitle: details?.jobOpening?.title || "",
    hiringManagerId: details?.jobOpening?.hiringManagerId || undefined,
    interviewStages: details?.interviewStages,
    stageInterviewers: moveStageInterviewers,
    isLoadingStageInterviewers: isLoadingMoveStageInterviewers,
    people: movePeople,
    additionalInvalidateKeys: applicationId
      ? [
          orpc.applications.getDetails.queryOptions({ input: { id: applicationId } })
            .queryKey as unknown as string[],
          orpc.applications.getNotes.queryOptions({ input: { id: applicationId } })
            .queryKey as unknown as string[],
        ]
      : undefined,
    canManage,
  });

  const createNoteMutation = useMutation({
    mutationFn: async ({ content, attachments }: { content: string; attachments?: string[] }) => {
      return rpcClient.applications.createNote({
        id: applicationId!,
        content,
        type: "free",
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getNotes.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      setNewNoteContent("");
      setPendingAttachments((prev) => ({ ...prev, freeNote: [] }));
      toast({ title: "Nota adicionada" });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível adicionar a nota.",
        variant: "destructive",
      });
    },
  });

  const createNoteReplyMutation = useMutation({
    mutationFn: async ({
      parentId,
      content,
      attachments,
    }: {
      parentId: string;
      content: string;
      attachments?: string[];
    }) => {
      return rpcClient.applications.createNote({
        id: applicationId!,
        content,
        type: "interview_note_reply",
        parentId,
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getNotes.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      setReplyingToNoteId(null);
      setNoteReplyContent((prev) => ({ ...prev, [variables.parentId]: "" }));
      setPendingAttachments((prev) => ({ ...prev, [`noteReply_${variables.parentId}`]: [] }));
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível adicionar o comentário.",
        variant: "destructive",
      });
    },
  });

  const createInterviewNoteMutation = useMutation({
    mutationFn: async ({ content, attachments }: { content: string; attachments?: string[] }) => {
      return rpcClient.applications.createNote({
        id: applicationId!,
        content,
        type: "interview_note",
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getNotes.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      setNewInterviewNoteContent("");
      setPendingAttachments((prev) => ({ ...prev, interviewNote: [] }));
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível adicionar o comentário.",
        variant: "destructive",
      });
    },
  });

  const createReplyMutation = useMutation({
    mutationFn: async ({
      interviewId,
      content,
      attachments,
    }: {
      interviewId: string;
      content: string;
      attachments?: string[];
    }) => {
      return rpcClient.applications.createNote({
        id: applicationId!,
        content,
        type: "interview_reply",
        interviewId,
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getNotes.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      setReplyingToInterviewId(null);
      setReplyContent((prev) => ({ ...prev, [variables.interviewId]: "" }));
      setPendingAttachments((prev) => ({ ...prev, [`reply_${variables.interviewId}`]: [] }));
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível adicionar o comentário.",
        variant: "destructive",
      });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({ noteId, content }: { noteId: string; content: string }) => {
      return rpcClient.applications.updateNote({ id: applicationId!, noteId, content });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getNotes.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      setEditingNoteId(null);
      setEditingNoteContent("");
      toast({ title: "Nota atualizada" });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível atualizar a nota.",
        variant: "destructive",
      });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      return rpcClient.applications.deleteNote({ id: applicationId!, noteId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getNotes.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      toast({ title: "Nota removida" });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível remover a nota.",
        variant: "destructive",
      });
    },
  });

  const deleteApplicationMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.applications.delete({ id: applicationId! });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      queryClient.invalidateQueries({ queryKey: orpc.interviews.mine.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      onOpenChange(false);
      toast({
        title: "Candidato removido",
        description: "O candidato e todos os dados associados foram removidos.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível remover o candidato.",
        variant: "destructive",
      });
    },
  });

  const deleteInterviewTarget = deleteInterviewId
    ? details?.interviews.find((i) => i.id === deleteInterviewId)
    : null;

  const deleteInterviewMutation = useMutation({
    mutationFn: async () => {
      if (!deleteInterviewId) return;
      return rpcClient.interviews.delete({ id: deleteInterviewId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.interviews.mine.queryOptions().queryKey });
      setDeleteInterviewId(null);
      setDeleteConfirmCode("");
      setDeleteExpectedCode("");
      toast({
        title: "Entrevista excluída",
        description: "A entrevista e seus dados associados foram removidos.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível excluir a entrevista.",
        variant: "destructive",
      });
    },
  });

  const currentMoveInterview = details?.interviews.find((i) => i.id === moveInterviewId);

  const moveInterviewMutation = useMutation({
    mutationFn: async ({
      interviewId,
      interviewStageId,
    }: {
      interviewId: string;
      interviewStageId: string;
    }) => {
      return rpcClient.interviews.changeStage({
        id: interviewId,
        interviewStageId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.interviews.mine.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      setMoveInterviewId(null);
      setMoveTargetStageId("");
      toast({
        title: "Entrevista movida",
        description: "A entrevista foi movida para a nova etapa.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao mover entrevista",
        variant: "destructive",
      });
    },
  });

  const handleScheduleInterview = useCallback((stageId: string) => {
    setScheduleAnotherStageId(stageId);
  }, []);

  const uploadFilesToKey = useCallback(
    async (inputKey: string, files: File[]) => {
      for (const file of files) {
        const result = await uploadFile(file);
        if (result) {
          setPendingAttachments((prev) => ({
            ...prev,
            [inputKey]: [...(prev[inputKey] || []), result.objectPath],
          }));
        }
      }
    },
    [uploadFile, setPendingAttachments],
  );

  const handleAttachPhoto = useCallback(
    async (inputKey: string) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.multiple = true;
      input.onchange = async () => {
        if (!input.files || input.files.length === 0) return;
        await uploadFilesToKey(inputKey, Array.from(input.files));
      };
      input.click();
    },
    [uploadFilesToKey],
  );

  const handlePaste = useCallback(
    (inputKey: string, e: React.ClipboardEvent) => {
      const images = Array.from(e.clipboardData.items)
        .filter((item) => item.type.startsWith("image/"))
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      if (images.length === 0) return;
      e.preventDefault();
      uploadFilesToKey(inputKey, images);
    },
    [uploadFilesToKey],
  );

  const removePendingAttachment = useCallback(
    (inputKey: string, index: number) => {
      setPendingAttachments((prev) => ({
        ...prev,
        [inputKey]: (prev[inputKey] || []).filter((_, i) => i !== index),
      }));
    },
    [setPendingAttachments],
  );

  // Read from ref (always current) to avoid stale-closure race when submitting
  const getAttachmentsForKey = useCallback(
    (key: string) =>
      (pendingAttachmentsRef.current[key] ?? []).filter((p): p is string => typeof p === "string"),
    [],
  );

  const reschedule = useRescheduleInterview({
    additionalInvalidateKeys: [
      ...(applicationId
        ? [
            orpc.applications.getDetails.queryOptions({ input: { id: applicationId } })
              .queryKey as unknown as string[],
          ]
        : []),
      ...(details?.jobOpening?.id
        ? [
            orpc.applications.getJobKanban.queryOptions({
              input: { jobOpeningId: details.jobOpening.id },
            }).queryKey as unknown as string[],
          ]
        : []),
    ],
  });

  const openFeedbackDialog = (
    interview: InterviewWithDetails,
    editing = false,
    groupFeedback?: GroupFeedback,
  ) => {
    const fb = editing ? groupFeedback : undefined;
    setFeedbackDialogInterview({
      interviewId: interview.id,
      interviewStageId: interview.interviewStageId,
      interviewerGroupId: interview.interviewerGroupId,
      applicantName: details?.applicant?.fullName,
      stageName: interview.stage?.stageName,
      jobOpeningId: details?.jobOpening?.id,
      jobOpeningTitle: details?.jobOpening?.title,
      applicationId: applicationId || undefined,
      allowAutoAdvance: details?.jobOpening?.allowAutoAdvance !== false,
      firefliesRecordingUrl: interview.firefliesRecordingUrl,
      meetingLink: interview.meetingLink,
      ...(editing
        ? {
            gcaVerdict: (fb?.gcaVerdict ?? interview.gcaVerdict) as any,
            gcaNotes: fb?.gcaNotes ?? interview.gcaNotes ?? "",
            roleKnowledgeVerdict: (fb?.roleKnowledgeVerdict ??
              interview.roleKnowledgeVerdict) as any,
            roleKnowledgeNotes: fb?.roleKnowledgeNotes ?? interview.roleKnowledgeNotes ?? "",
            compWayVerdict: (fb?.compWayVerdict ?? interview.compWayVerdict) as any,
            compWayNotes: fb?.compWayNotes ?? interview.compWayNotes ?? "",
            candidateExcitementScore:
              fb?.candidateExcitementScore ?? interview.candidateExcitementScore ?? null,
            candidateExcitementNote:
              fb?.candidateExcitementNote ?? interview.candidateExcitementNote ?? "",
            generalComments: fb?.generalComments ?? interview.generalComments ?? "",
            notesForNextInterviewer:
              fb?.notesForNextInterviewer ?? interview.notesForNextInterviewer ?? "",
            recommendation: fb?.recommendation ?? interview.recommendation,
            customCriteriaScores:
              fb?.customCriteriaScores ?? interview.customCriteriaScores ?? null,
          }
        : {}),
    });
    setIsEditingFeedback(editing);
    setFeedbackDialogOpen(true);
  };

  const sendOfferMutation = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.sendToCandidate({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      toast({
        title: "Oferta enviada!",
        description: "O candidato receberá um email com a oferta.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao enviar oferta",
        description: error.message || "Não foi possível enviar a oferta.",
        variant: "destructive",
      });
    },
  });

  const markOfferAsSentMutation = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.markAsSent({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      toast({
        title: "Oferta marcada como enviada",
        description: "Status atualizado sem envio de email.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível marcar como enviada.",
        variant: "destructive",
      });
    },
  });

  const markOfferAsAcceptedMutation = useMutation({
    mutationFn: async ({ offerId, startDate }: { offerId: string; startDate: string }) => {
      return rpcClient.offers.markAsAccepted({ id: offerId, startDate });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      toast({ title: "Oferta aceita!", description: "Candidato convertido em colaborador." });
      setAcceptOfferId(null);
      setAcceptStartDate("");
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível marcar como aceita.",
        variant: "destructive",
      });
    },
  });

  const deleteOfferMutation = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.delete({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      toast({ title: "Oferta excluída" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível excluir a oferta.",
        variant: "destructive",
      });
    },
  });

  const cancelOfferMutation = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.cancelOffer({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      setCancelOfferId(null);
      toast({ title: "Oferta cancelada" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível cancelar a oferta.",
        variant: "destructive",
      });
    },
  });

  const rejectOfferManualMutation = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.rejectManual({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      setRejectManualOfferId(null);
      toast({ title: "Oferta marcada como rejeitada" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível marcar a oferta como rejeitada.",
        variant: "destructive",
      });
    },
  });

  const refreshTemplateMutation = useMutation({
    mutationFn: async (offerId: string) => {
      return rpcClient.offers.refreshTemplate({ id: offerId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      toast({
        title: "Template atualizado",
        description: "A carta de oferta foi atualizada com o template mais recente.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível atualizar o template.",
        variant: "destructive",
      });
    },
  });

  const markNotifiedMutation = useMutation({
    mutationFn: async (interviewId: string) => {
      return rpcClient.interviews.markNotified({ id: interviewId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      toast({ title: "Candidato marcado como comunicado" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível marcar como comunicado.",
        variant: "destructive",
      });
    },
  });

  const markRejectionNotifiedMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.applications.markRejectionNotified({ id: applicationId! });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      toast({ title: "Candidato marcado como comunicado" });
    },
    onError: (error: Error) => {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    },
  });

  const updateInterviewStatusMutation = useMutation({
    mutationFn: async ({
      interviewId,
      status,
    }: {
      interviewId: string;
      status: "pending" | "scheduled" | "finished";
    }) => {
      return rpcClient.interviews.updateStatus({ id: interviewId, status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      toast({ title: "Status da entrevista atualizado" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível alterar o status da entrevista.",
        variant: "destructive",
      });
    },
  });

  const deleteRcMutation = useMutation({
    ...orpc.referenceChecks.delete.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      setDeletingRcId(null);
      toast({ title: "Reference check removido" });
    },
    onError: () => toast({ title: "Erro ao deletar reference check", variant: "destructive" }),
  });

  const createRcMutation = useMutation({
    ...orpc.referenceChecks.create.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      setRcDrawerOpen(false);
      setEditingRc(null);
      toast({ title: "Reference check criado" });
    },
    onError: () => toast({ title: "Erro ao criar reference check", variant: "destructive" }),
  });

  const updateRcMutation = useMutation({
    ...orpc.referenceChecks.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      if (details?.jobOpening?.id) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({
            input: { jobOpeningId: details.jobOpening.id },
          }).queryKey,
        });
      }
      setRcDrawerOpen(false);
      setEditingRc(null);
      toast({ title: "Reference check atualizado" });
    },
    onError: () => toast({ title: "Erro ao atualizar reference check", variant: "destructive" }),
  });

  const interviewStatusTooltips: Record<string, string> = {
    "Não comunicado": "O candidato ainda não foi comunicado sobre esta entrevista",
    "Aguardando Agendamento": "O candidato foi comunicado e aguarda o agendamento",
    Agendado: "A entrevista foi agendada com data e hora definidas",
    "Aguardando Avaliação": "A entrevista foi realizada, aguardando avaliação do entrevistador",
    Finalizado: "Entrevista concluída com avaliação registrada",
  };

  const getInterviewStatusBadge = (interview: InterviewWithDetails) => {
    const contextLabel = (interview.contextStatus as InterviewContextLabel) ?? "Não comunicado";
    const tooltip = interviewStatusTooltips[contextLabel];
    const canEdit = canManage;

    const statusOptions: { value?: string; label: string; action?: () => void }[] = [];
    if (canEdit) {
      if (contextLabel === "Não comunicado") {
        statusOptions.push({
          label: "Aguardando Agendamento",
          action: () => markNotifiedMutation.mutate(interview.id),
        });
      }
      if (interview.status === "pending") {
        statusOptions.push({ value: "scheduled", label: "Agendado" });
        statusOptions.push({ value: "finished", label: "Aguardando Avaliação" });
      } else if (interview.status === "scheduled") {
        statusOptions.push({ value: "finished", label: "Aguardando Avaliação" });
      }
    }

    const badge = (
      <Badge
        variant="outline"
        className={`text-xs ${interviewStatusColors[contextLabel] || ""} ${statusOptions.length > 0 ? "cursor-pointer gap-1" : ""}`}
        data-testid={`badge-interview-status-${interview.id}`}
      >
        {contextLabel}
        {statusOptions.length > 0 && <ChevronDown className="w-2.5 h-2.5 opacity-60" />}
      </Badge>
    );

    const dropdownItems = (
      <DropdownMenuContent align="start">
        {statusOptions.map((opt) => (
          <DropdownMenuItem
            key={opt.value ?? opt.label}
            disabled={updateInterviewStatusMutation.isPending || markNotifiedMutation.isPending}
            onClick={(e) => {
              e.stopPropagation();
              opt.action
                ? opt.action()
                : updateInterviewStatusMutation.mutate({
                    interviewId: interview.id,
                    status: opt.value!,
                  });
            }}
            data-testid={`option-interview-status-${opt.value ?? "notify"}-${interview.id}`}
          >
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    );

    if (statusOptions.length > 0 && tooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <DropdownMenu>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <span className="inline-flex">{badge}</span>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              {dropdownItems}
            </DropdownMenu>
            <TooltipContent side="top" className="text-xs max-w-xs">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (statusOptions.length > 0) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <span className="inline-flex">{badge}</span>
          </DropdownMenuTrigger>
          {dropdownItems}
        </DropdownMenu>
      );
    }

    if (tooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{badge}</TooltipTrigger>
            <TooltipContent side="top" className="text-xs max-w-xs">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return badge;
  };

  const applicationStatusOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [
      { value: "applied", label: "Inscrito" },
      // Pré-entrevista: sem estas opções o Select fica em branco quando o
      // candidato está priorizado/despriorizado (o valor atual não casa com
      // nenhuma opção). Elas são colunas válidas do pipeline e do move.
      { value: "prioritized", label: "Priorizado" },
      { value: "deprioritized", label: "Despriorizado" },
    ];
    if (details?.interviewStages) {
      details.interviewStages
        .filter((s) => !s.hidden)
        .sort((a, b) => a.stageOrder - b.stageOrder)
        .forEach((stage) => {
          opts.push({ value: `stage-${stage.id}`, label: stage.stageName });
        });
    }
    opts.push(
      { value: "reference_checks", label: "Reference Checks" },
      { value: "dossier", label: "Committee" },
      { value: "offer", label: "Oferta" },
      { value: "hired", label: "Contratado" },
      { value: "rejected", label: "Rejeitado" },
      { value: "offer_rejected", label: "Offer Rejected" },
    );
    return opts;
  }, [details?.interviewStages]);

  const handleStatusChange = useCallback(
    async (val: string) => {
      if (!applicationId || !details) return;
      const targetInterviewStageId = val.startsWith("stage-") ? val.replace("stage-", "") : null;
      const isInterviewStage = val.startsWith("stage-");
      const stage = isInterviewStage
        ? details.interviewStages?.find((s) => s.id === targetInterviewStageId)
        : undefined;
      const targetTitle = applicationStatusOptions.find((o) => o.value === val)?.label || val;

      await moveApp.initMove({
        applicationId,
        targetColumn: val,
        targetInterviewStageId,
        applicantName: details.applicant?.fullName || "Candidato",
        targetColumnTitle: targetTitle,
        isInterviewStage,
        stage,
      });
    },
    [applicationId, details, applicationStatusOptions, moveApp],
  );

  const currentStatusValue = useMemo(() => {
    if (details?.status === "interviewing" && details.currentInterviewStageId) {
      return `stage-${details.currentInterviewStageId}`;
    }
    return details?.status || "applied";
  }, [details?.status, details?.currentInterviewStageId]);

  const getStatusLabel = (status: string) => {
    return applicationStatusOptions.find((o) => o.value === status)?.label || status;
  };

  const formatDateTime = (date: Date | string | null) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Human labels for SYSTEM-triggered history events (see schema's
  // APPLICATION_HISTORY_SYSTEM_SOURCES). Falls back to "Sistema".
  const SYSTEM_SOURCE_LABELS: Record<string, string> = {
    ai_screening: "Triagem de IA",
    auto_advance: "Avanço automático",
  };

  type HistoryEntry = {
    eventType?: string | null;
    oldStatus: string | null;
    newStatus: string;
    oldStepName: string | null;
    newStepName: string | null;
    changedBy: string;
    changedByName?: string | null;
    systemSource?: string | null;
  };

  type HistoryDescription = { visible: boolean; actor: string } & (
    | { kind: "move"; oldLabel: string | null; newLabel: string }
    | { kind: "event"; text: string }
  );

  // Turns a raw history row into a renderable description. Only `stage_move`
  // (and legacy rows) render as a "Y → Z" arrow; interview-scoped events get
  // their own sentence so they never masquerade as a pipeline move.
  const describeHistoryEntry = (entry: HistoryEntry): HistoryDescription => {
    const actor =
      entry.changedBy === "SYSTEM"
        ? (entry.systemSource && SYSTEM_SOURCE_LABELS[entry.systemSource]) || "Sistema"
        : entry.changedByName || "Usuário";

    switch (entry.eventType) {
      case "interview_created":
        return {
          visible: true,
          actor,
          kind: "event",
          text: `Criou a entrevista — ${entry.newStepName ?? "Entrevista"}`,
        };
      case "interview_stage_changed":
        return {
          visible: true,
          actor,
          kind: "event",
          text: `Moveu a entrevista de ${entry.oldStepName ?? "?"} para ${entry.newStepName ?? "?"}`,
        };
      case "interviewer_changed":
        return {
          visible: true,
          actor,
          kind: "event",
          text: `Trocou o entrevistador de ${entry.oldStepName ?? "?"} para ${entry.newStepName ?? "?"}`,
        };
      default: {
        // stage_move + legacy rows
        const oldLabel =
          entry.oldStepName || (entry.oldStatus ? getStatusLabel(entry.oldStatus) : null);
        const newLabel = entry.newStepName || getStatusLabel(entry.newStatus);
        return { visible: oldLabel !== newLabel, actor, kind: "move", oldLabel, newLabel };
      }
    }
  };

  const renderAttachments = (attachments?: string[] | null) => {
    const valid = attachments?.filter((p): p is string => typeof p === "string") ?? [];
    if (valid.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {valid.map((path, i) => (
          <a key={path} href={path} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={path}
              alt={`Anexo ${i + 1}`}
              className="rounded-md border border-border max-h-40 max-w-[200px] object-cover hover:opacity-80 transition-opacity"
            />
          </a>
        ))}
      </div>
    );
  };

  const renderPendingAttachments = (inputKey: string) => {
    const attachments = pendingAttachments[inputKey];
    if (!attachments || attachments.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-1">
        {attachments.map((path, i) => (
          <div key={path} className="relative group/att">
            <img
              src={path}
              alt={`Anexo ${i + 1}`}
              className="rounded-md border border-border h-16 w-16 object-cover"
            />
            <button
              type="button"
              onClick={() => removePendingAttachment(inputKey, i)}
              className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover/att:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderAttachButton = (inputKey: string) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="self-end shrink-0 h-8 w-8"
      onClick={() => handleAttachPhoto(inputKey)}
      disabled={isUploadingFile}
      title="Anexar foto"
      data-testid={`button-attach-photo-${inputKey}`}
    >
      {isUploadingFile ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <ImagePlus className="w-4 h-4" />
      )}
    </Button>
  );

  const sectionIconColors: Record<string, string> = {
    ofertas: "bg-muted text-muted-foreground",
    dossie: "bg-muted text-muted-foreground",
    notas: "bg-muted text-muted-foreground",
    entrevistas: "bg-muted text-muted-foreground",
    comentarios: "bg-muted text-muted-foreground",
    historico: "bg-muted text-muted-foreground",
  };

  const SectionHeader = ({
    id,
    icon,
    title,
    count,
    tooltip,
  }: {
    id: string;
    icon: React.ReactNode;
    title: string;
    count?: number;
    tooltip?: string;
  }) => (
    <button
      type="button"
      onClick={() => toggleSection(id)}
      className="flex items-center justify-between w-full py-3.5 px-1 group cursor-pointer hover:bg-muted/40 -mx-1 rounded-md transition-colors"
    >
      <div className="flex items-center gap-2.5">
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center ${sectionIconColors[id] || "bg-muted text-muted-foreground"}`}
        >
          {icon}
        </div>
        <span className="text-[13px] font-semibold">{title}</span>
        {tooltip && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={(e) => e.stopPropagation()}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px] text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {count !== undefined && (
          <span className="text-xs text-muted-foreground font-medium">{count}</span>
        )}
      </div>
      <ChevronDown
        className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${collapsedSections.has(id) ? "" : "rotate-180"}`}
      />
    </button>
  );

  const canShowOffersSection =
    canManage &&
    (["offer", "hired"].includes(details?.status ?? "") || (details?.offers?.length ?? 0) > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-3 pb-1 flex-shrink-0 space-y-0">
          <DialogTitle className="text-base">Detalhes do Candidato</DialogTitle>
          {details?.jobOpening?.title && (
            <p className="text-xs text-muted-foreground">{details.jobOpening.title}</p>
          )}
        </DialogHeader>

        {isDetailsError ? (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-destructive">
            <p className="text-sm">Não foi possível carregar os detalhes do candidato.</p>
            <Button variant="outline" size="sm" onClick={refetchDetails}>
              Tentar novamente
            </Button>
          </div>
        ) : !details ? (
          <div className="space-y-4 px-6 py-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <>
            {/* Two-panel body */}
            <div className="flex flex-1 overflow-hidden">
              {/* Left panel */}
              <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {/* Profile Header — fixed at top */}
                <div className="px-6 pb-3 flex-shrink-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3.5 min-w-0 flex-1">
                      <Avatar className="h-14 w-14 shrink-0">
                        <AvatarFallback className="bg-[hsl(var(--coral-mist))] text-[hsl(var(--comp-coral))] text-lg font-medium">
                          {details.applicant?.fullName
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase() || "??"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <Link
                            href={
                              details.applicant?.id ? `/applicants/${details.applicant.id}` : "#"
                            }
                            className="text-[17px] font-semibold tracking-tight hover:underline hover:text-primary transition-colors"
                          >
                            {details.applicant?.fullName || "Candidato"}
                          </Link>
                          {/* Dropdown 1: Etapa */}
                          <Select
                            value={currentStatusValue}
                            onValueChange={(val) => handleStatusChange(val)}
                            disabled={moveApp.isValidating || moveApp.moveMutation.isPending}
                          >
                            <SelectTrigger
                              className="h-7 w-auto gap-1 text-xs font-medium px-2"
                              data-testid="select-application-status"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {applicationStatusOptions.map((opt) => (
                                <SelectItem
                                  key={opt.value}
                                  value={opt.value}
                                  data-testid={`option-status-${opt.value}`}
                                >
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Candidate excitement pill — sits next to the
                              status dropdown. Compact by default; clicking
                              opens a popover with trajectory + last reading,
                              and a link to jump to the Entrevistas section. */}
                          <CandidateExcitementHeaderBlock
                            scores={candidateExcitementSeries}
                            applicationId={applicationId ?? ""}
                            onShowHistory={handleShowExcitementHistory}
                          />

                          {/* Dropdown 2: Status da etapa (só quando em interviewing) */}
                          {details.status === "interviewing" &&
                            (() => {
                              const currentInterview = details.interviews
                                .filter(
                                  (i) => i.interviewStageId === details.currentInterviewStageId,
                                )
                                .sort(
                                  (a, b) =>
                                    new Date(b.createdAt).getTime() -
                                    new Date(a.createdAt).getTime(),
                                )[0];
                              if (!currentInterview) return null;
                              const currentLabel =
                                (currentInterview.contextStatus as InterviewContextLabel) ??
                                "Não comunicado";
                              const isNotNotified = currentLabel === "Não comunicado";
                              const statusOptions: {
                                value: string;
                                label: string;
                                action?: () => void;
                              }[] = [];
                              if (isNotNotified) {
                                statusOptions.push({
                                  value: "notify",
                                  label: "Aguardando Agendamento",
                                  action: () => markNotifiedMutation.mutate(currentInterview.id),
                                });
                              }
                              if (currentInterview.status === "pending") {
                                statusOptions.push({ value: "scheduled", label: "Agendado" });
                                statusOptions.push({
                                  value: "finished",
                                  label: "Aguardando Avaliação",
                                });
                              } else if (currentInterview.status === "scheduled") {
                                statusOptions.push({
                                  value: "finished",
                                  label: "Aguardando Avaliação",
                                });
                              }
                              return (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={`inline-flex items-center gap-1 h-7 px-2 text-xs font-medium rounded-md border ${interviewStatusColors[currentLabel] || ""}`}
                                      disabled={
                                        updateInterviewStatusMutation.isPending ||
                                        markNotifiedMutation.isPending
                                      }
                                      data-testid="select-interview-status"
                                    >
                                      {currentLabel}
                                      <ChevronDown className="w-3 h-3 opacity-50" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  {statusOptions.length > 0 && (
                                    <DropdownMenuContent align="start">
                                      {statusOptions.map((opt) => (
                                        <DropdownMenuItem
                                          key={opt.value}
                                          onClick={() =>
                                            opt.action
                                              ? opt.action()
                                              : updateInterviewStatusMutation.mutate({
                                                  interviewId: currentInterview.id,
                                                  status: opt.value,
                                                })
                                          }
                                          data-testid={`option-interview-status-${opt.value}`}
                                        >
                                          {opt.label}
                                        </DropdownMenuItem>
                                      ))}
                                    </DropdownMenuContent>
                                  )}
                                </DropdownMenu>
                              );
                            })()}

                          {/* Dropdown 2: Comunicado (só quando rejeitado) */}
                          {details.status === "rejected" &&
                            (() => {
                              const isComunicado = !!details.rejectionFeedbackSentAt;
                              const label = isComunicado ? "Comunicado" : "Não comunicado";
                              const colors =
                                "border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400";
                              if (isComunicado) {
                                return (
                                  <span
                                    className={`inline-flex items-center h-7 px-2 text-xs font-medium rounded-md border ${colors}`}
                                  >
                                    {label}
                                  </span>
                                );
                              }
                              return (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={`inline-flex items-center gap-1 h-7 px-2 text-xs font-medium rounded-md border ${colors}`}
                                      disabled={markRejectionNotifiedMutation.isPending}
                                      data-testid="select-rejection-comunicado"
                                    >
                                      {label}
                                      <ChevronDown className="w-3 h-3 opacity-50" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="start">
                                    <DropdownMenuItem
                                      onClick={() => markRejectionNotifiedMutation.mutate()}
                                      data-testid="option-mark-rejection-notified"
                                    >
                                      Marcar como Comunicado
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => setIsRejectionEmailDialogOpen(true)}
                                      data-testid="option-send-rejection-email"
                                    >
                                      Enviar Email de Feedback
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              );
                            })()}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
                          <CopyToClipboard
                            text={details.applicant?.email || ""}
                            toastMessage="Email copiado!"
                            icon={<Mail className="w-3.5 h-3.5" />}
                            label={details.applicant?.email}
                          />
                          {details.applicant?.phone && (
                            <CopyToClipboard
                              text={details.applicant.phone}
                              toastMessage="Telefone copiado!"
                              icon={<Phone className="w-3.5 h-3.5" />}
                              label={details.applicant.phone}
                            />
                          )}
                          {details.applicant?.linkedinUrl && (
                            <a
                              href={ensureExternalUrl(details.applicant.linkedinUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-blue-600 hover:underline"
                              data-testid="link-linkedin"
                            >
                              <Linkedin className="w-3.5 h-3.5" />
                              LinkedIn
                            </a>
                          )}
                          {details.resumePath && (
                            <a
                              href={`/api/applications/${details.id}/resume`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-blue-600 hover:underline"
                              data-testid="link-resume"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              Currículo
                            </a>
                          )}
                        </div>
                        {details.status === "rejected" && details.rejectionReason && (
                          <div
                            className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs"
                            data-testid="text-rejection-reason"
                          >
                            <span className="font-medium text-red-700 dark:text-red-300">
                              Motivo interno da rejeição:{" "}
                            </span>
                            <span className="text-red-600 dark:text-red-400">
                              {details.rejectionReason}
                            </span>
                            {details.rejectionFeedbackSentAt && (
                              <span className="ml-2 text-red-400 dark:text-red-500">
                                · Comunicado em {formatDateTime(details.rejectionFeedbackSentAt)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid="button-applicant-actions"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => setIsEditOpen(true)}
                          data-testid="button-edit-applicant-modal"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-2" />
                          Editar candidato
                        </DropdownMenuItem>
                        {canManage && (
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setShowDeleteDialog(true)}
                            data-testid="button-delete-application"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-2" />
                            Remover candidato
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                      <AlertDialogContent data-testid="dialog-confirm-delete-application">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remover candidato</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tem certeza que deseja remover este candidato? Esta ação é irreversível
                            e apagará todas as entrevistas, histórico e notas associadas.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteApplicationMutation.mutate()}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteApplicationMutation.isPending}
                            data-testid="button-confirm-delete"
                          >
                            {deleteApplicationMutation.isPending ? "Removendo..." : "Remover"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog
                      open={!!deleteDefId}
                      onOpenChange={(open) => !open && setDeleteDefId(null)}
                    >
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir propriedade?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação removerá a propriedade e todos os seus valores em todos os
                            candidatos deste processo. Não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => {
                              if (deleteDefId) deleteDefMutation.mutate(deleteDefId);
                              setDeleteDefId(null);
                            }}
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog
                      open={!!deletingRcId}
                      onOpenChange={(open) => !open && setDeletingRcId(null)}
                    >
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Deletar reference check?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Essa ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() =>
                              deletingRcId &&
                              deleteRcMutation.mutate({ id: deletingRcId, applicationId })
                            }
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteRcMutation.isPending}
                          >
                            {deleteRcMutation.isPending ? "Deletando..." : "Deletar"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog
                      open={!!sendConfirmOfferId}
                      onOpenChange={(open) => !open && setSendConfirmOfferId(null)}
                    >
                      <AlertDialogContent data-testid="dialog-confirm-send-offer">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Enviar oferta ao candidato?</AlertDialogTitle>
                          <AlertDialogDescription>
                            A oferta será enviada para o email{" "}
                            <span className="font-semibold text-foreground">
                              {details?.applicant?.email || "não informado"}
                            </span>
                            . Deseja confirmar o envio?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="button-cancel-send-offer">
                            Cancelar
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              if (sendConfirmOfferId) {
                                sendOfferMutation.mutate(sendConfirmOfferId);
                              }
                              setSendConfirmOfferId(null);
                            }}
                            disabled={sendOfferMutation.isPending}
                            data-testid="button-confirm-send-offer"
                          >
                            {sendOfferMutation.isPending ? "Enviando..." : "Confirmar Envio"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog
                      open={!!rejectManualOfferId}
                      onOpenChange={(open) => !open && setRejectManualOfferId(null)}
                    >
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Marcar oferta como rejeitada?</AlertDialogTitle>
                          <AlertDialogDescription>
                            A oferta será marcada como rejeitada e ficará registrada no histórico.
                            Após rejeitar, será possível criar uma nova oferta para este candidato.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Voltar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              if (rejectManualOfferId)
                                rejectOfferManualMutation.mutate(rejectManualOfferId);
                            }}
                            disabled={rejectOfferManualMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {rejectOfferManualMutation.isPending
                              ? "Rejeitando..."
                              : "Marcar como Rejeitada"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog
                      open={!!cancelOfferId}
                      onOpenChange={(open) => !open && setCancelOfferId(null)}
                    >
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancelar oferta?</AlertDialogTitle>
                          <AlertDialogDescription>
                            A oferta será cancelada e não ficará mais disponível para o candidato.
                            Após cancelar, será possível criar uma nova oferta para este candidato.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Voltar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              if (cancelOfferId) cancelOfferMutation.mutate(cancelOfferId);
                            }}
                            disabled={cancelOfferMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {cancelOfferMutation.isPending ? "Cancelando..." : "Cancelar Oferta"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Dialog
                      open={!!acceptOfferId}
                      onOpenChange={(open) => {
                        if (!open) {
                          setAcceptOfferId(null);
                          setAcceptStartDate("");
                        }
                      }}
                    >
                      <DialogContent data-testid="dialog-confirm-mark-accepted-modal">
                        <DialogHeader>
                          <DialogTitle>Marcar oferta como aceita?</DialogTitle>
                          <DialogDescription>
                            Informe a data de início do colaborador.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="accept-start-date">
                              Data de Início <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="accept-start-date"
                              type="date"
                              value={acceptStartDate}
                              onChange={(e) => setAcceptStartDate(e.target.value)}
                              autoFocus
                              data-testid="input-accept-start-date"
                            />
                            <p className="text-sm text-muted-foreground">
                              Define o dia 1 do onboarding e o início do ciclo de performance.
                            </p>
                          </div>
                          <div className="rounded-md bg-[hsl(var(--amber-mist))] border border-[hsl(var(--amber-state))]/30 p-3 text-sm text-[hsl(var(--ink-primary))]">
                            <p className="font-medium mb-1">Esta ação é irreversível e irá:</p>
                            <ul className="list-disc list-inside space-y-0.5 text-xs">
                              <li>Criar o registro do colaborador</li>
                              <li>Mover a candidatura para "Contratado"</li>
                              <li>Disparar o plano de onboarding</li>
                              <li>Criar o primeiro ciclo de performance (3 meses)</li>
                            </ul>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setAcceptOfferId(null);
                              setAcceptStartDate("");
                            }}
                          >
                            Cancelar
                          </Button>
                          <Button
                            onClick={() => {
                              if (acceptOfferId) {
                                markOfferAsAcceptedMutation.mutate({
                                  offerId: acceptOfferId,
                                  startDate: acceptStartDate,
                                });
                              }
                            }}
                            disabled={markOfferAsAcceptedMutation.isPending || !acceptStartDate}
                            data-testid="button-confirm-mark-accepted-modal"
                          >
                            {markOfferAsAcceptedMutation.isPending
                              ? "Salvando..."
                              : "Confirmar Aceite"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Quick Nav */}
                <div className="flex gap-0.5 px-7 py-1.5 border-b border-border/40 flex-wrap flex-shrink-0">
                  {canShowOffersSection && (
                    <button
                      type="button"
                      onClick={() => scrollToSection("ofertas")}
                      className="px-3 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                    >
                      Ofertas{" "}
                      {details.offers?.length ? (
                        <span className="ml-1 bg-muted text-muted-foreground px-1 rounded text-[10px]">
                          {details.offers.length}
                        </span>
                      ) : null}
                    </button>
                  )}
                  {(["dossier", "offer", "hired"].includes(details.status) ||
                    (details.status === "rejected" && details.dossierDecision)) && (
                    <button
                      type="button"
                      onClick={() => scrollToSection("dossie")}
                      className="px-3 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                    >
                      Dossiê
                    </button>
                  )}
                  {(["reference_checks", "dossier", "offer", "hired"].includes(details.status) ||
                    (details.referenceChecks?.length ?? 0) > 0) && (
                    <button
                      type="button"
                      onClick={() => scrollToSection("reference-checks")}
                      className="px-3 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                    >
                      Reference Checks{" "}
                      {(details.referenceChecks?.length ?? 0) > 0 && (
                        <span className="ml-1 bg-muted text-muted-foreground px-1 rounded text-[10px]">
                          {details.referenceChecks!.length}
                        </span>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => scrollToSection("notas")}
                    className="px-3 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                  >
                    Notas de entrevistadores
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollToSection("entrevistas")}
                    className="px-3 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                  >
                    Entrevistas{" "}
                    <span className="ml-1 bg-muted text-muted-foreground px-1 rounded text-[10px]">
                      {details.interviews.length}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollToSection("comentarios")}
                    className="px-3 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                  >
                    Comentários{" "}
                    <span className="ml-1 bg-muted text-muted-foreground px-1 rounded text-[10px]">
                      {(notes || []).filter((n) => n.type === "free" || !n.type).length}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollToSection("historico")}
                    className="px-3 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                  >
                    Histórico
                  </button>
                </div>

                <div ref={scrollBodyRef} className="flex-1 overflow-y-auto">
                  <div className="pb-4">
                    {/* Ofertas — aparece quando: offers.length > 0 OU status in [offer, hired] OU usuário tem permissão para criar */}
                    {canShowOffersSection && (
                      <div id="section-ofertas" className="border-b border-border/40 px-7">
                        <SectionHeader
                          id="ofertas"
                          icon={<DollarSign className="w-3.5 h-3.5" />}
                          title="Ofertas"
                          count={details.offers?.length || 0}
                        />
                        {!collapsedSections.has("ofertas") && (
                          <div className="pb-4">
                            {details.offers && details.offers.length > 0 ? (
                              <div className="space-y-3">
                                {details.offers.map((offer) => (
                                  <div key={offer.id} className="border rounded-lg overflow-hidden">
                                    {/* Header */}
                                    <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
                                      <div className="flex items-center gap-2">
                                        <StatusBadge status={offer.status} />
                                        <span className="text-xs text-muted-foreground">
                                          {formatDateTime(offer.createdAt)}
                                        </span>
                                      </div>
                                      {canManage && (
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <button
                                              type="button"
                                              className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                                              data-testid={`button-offer-menu-${offer.id}`}
                                            >
                                              <MoreHorizontal className="w-4 h-4" />
                                            </button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            <DropdownMenuItem
                                              onClick={() =>
                                                refreshTemplateMutation.mutate(offer.id)
                                              }
                                              disabled={refreshTemplateMutation.isPending}
                                            >
                                              <RefreshCw className="w-3.5 h-3.5 mr-2" />
                                              {refreshTemplateMutation.isPending
                                                ? "Atualizando..."
                                                : "Atualizar template"}
                                            </DropdownMenuItem>
                                            {(offer.status === "sent" ||
                                              offer.status === "approved") && (
                                              <DropdownMenuItem
                                                onClick={() => setAcceptOfferId(offer.id)}
                                              >
                                                <CheckCircle className="w-3.5 h-3.5 mr-2" />
                                                Marcar como Aceita
                                              </DropdownMenuItem>
                                            )}
                                            {offer.status !== "rejected" &&
                                              offer.status !== "cancelled" && (
                                                <>
                                                  <DropdownMenuSeparator />
                                                  {[
                                                    "waiting_approval",
                                                    "approved",
                                                    "sent",
                                                  ].includes(offer.status) && (
                                                    <DropdownMenuItem
                                                      className="text-destructive focus:text-destructive"
                                                      onClick={() =>
                                                        setRejectManualOfferId(offer.id)
                                                      }
                                                      disabled={rejectOfferManualMutation.isPending}
                                                    >
                                                      <XCircle className="w-3.5 h-3.5 mr-2" />
                                                      Recusar oferta
                                                    </DropdownMenuItem>
                                                  )}
                                                  <DropdownMenuItem
                                                    className="text-destructive focus:text-destructive"
                                                    onClick={() => setCancelOfferId(offer.id)}
                                                    disabled={cancelOfferMutation.isPending}
                                                  >
                                                    <XCircle className="w-3.5 h-3.5 mr-2" />
                                                    Cancelar Oferta
                                                  </DropdownMenuItem>
                                                </>
                                              )}
                                            {offer.status !== "accepted" && (
                                              <>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                  className="text-destructive focus:text-destructive"
                                                  onClick={() => setDeleteOfferId(offer.id)}
                                                >
                                                  <Trash2 className="w-3.5 h-3.5 mr-2" />
                                                  Excluir oferta
                                                </DropdownMenuItem>
                                              </>
                                            )}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      )}
                                    </div>

                                    {/* Info grid */}
                                    <div className="px-4 py-3 grid grid-cols-2 gap-3 text-sm">
                                      <div className="space-y-0.5">
                                        <p className="text-xs text-muted-foreground">
                                          Salário Base
                                        </p>
                                        <p className="font-semibold">
                                          {formatCurrency(Number(offer.salaryBase))}
                                        </p>
                                      </div>
                                      {offer.equityShares && (
                                        <div className="space-y-0.5">
                                          <p className="text-xs text-muted-foreground">Equity</p>
                                          <p className="font-medium">
                                            {offer.equityShares.toLocaleString()} shares
                                          </p>
                                        </div>
                                      )}
                                      {offer.signingBonus && (
                                        <div className="space-y-0.5">
                                          <p className="text-xs text-muted-foreground">
                                            Signing Bonus
                                          </p>
                                          <p className="font-medium">
                                            {formatCurrency(Number(offer.signingBonus))}
                                          </p>
                                        </div>
                                      )}
                                      {offer.performanceBonus && (
                                        <div className="space-y-0.5">
                                          <p className="text-xs text-muted-foreground">
                                            Bônus de Performance
                                          </p>
                                          <p className="font-medium">
                                            {formatCurrency(Number(offer.performanceBonus))}
                                          </p>
                                        </div>
                                      )}
                                      {offer.performanceBonusShares && (
                                        <div className="space-y-0.5">
                                          <p className="text-xs text-muted-foreground">
                                            Bônus de Performance (Shares)
                                          </p>
                                          <p className="font-medium">
                                            {formatSharesRange(
                                              offer.performanceBonusShares,
                                              offer.performanceBonusSharesMax,
                                            )}
                                          </p>
                                        </div>
                                      )}
                                    </div>

                                    {/* Rejection/Decline details (collapsible) */}
                                    {offer.status === "rejected" &&
                                      (offer.rejector || offer.rejectionReason) && (
                                        <div className="px-4 pb-3">
                                          <Collapsible>
                                            <CollapsibleTrigger className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline">
                                              <ChevronDown className="w-3 h-3" />
                                              Ver justificativa da rejeição
                                            </CollapsibleTrigger>
                                            <CollapsibleContent>
                                              <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-2.5 text-sm space-y-1">
                                                {offer.rejector && (
                                                  <p className="text-red-600 dark:text-red-400 text-xs">
                                                    Rejeitada por{" "}
                                                    <strong>{offer.rejector.fullName}</strong> em{" "}
                                                    {formatDateTime(offer.rejectedAt)}
                                                  </p>
                                                )}
                                                {offer.rejectionReason && (
                                                  <p className="text-red-600 dark:text-red-400 text-xs">
                                                    Motivo: {offer.rejectionReason}
                                                  </p>
                                                )}
                                              </div>
                                            </CollapsibleContent>
                                          </Collapsible>
                                        </div>
                                      )}

                                    {offer.status === "declined" && (
                                      <div className="px-4 pb-3">
                                        <Collapsible>
                                          <CollapsibleTrigger className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline">
                                            <ChevronDown className="w-3 h-3" />
                                            Ver detalhes da recusa
                                          </CollapsibleTrigger>
                                          <CollapsibleContent>
                                            <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-2.5 text-sm">
                                              <p className="text-red-600 dark:text-red-400 text-xs">
                                                Recusada pelo candidato
                                                {offer.respondedAt
                                                  ? ` em ${formatDateTime(offer.respondedAt)}`
                                                  : ""}
                                              </p>
                                            </div>
                                          </CollapsibleContent>
                                        </Collapsible>
                                      </div>
                                    )}

                                    {offer.status === "cancelled" && (
                                      <div className="px-4 pb-3">
                                        <Collapsible>
                                          <CollapsibleTrigger className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 hover:underline">
                                            <ChevronDown className="w-3 h-3" />
                                            Ver detalhes do cancelamento
                                          </CollapsibleTrigger>
                                          <CollapsibleContent>
                                            <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-2.5 text-sm space-y-1">
                                              {offer.rejector && (
                                                <p className="text-amber-600 dark:text-amber-400 text-xs">
                                                  Cancelada por{" "}
                                                  <strong>{offer.rejector.fullName}</strong> em{" "}
                                                  {formatDateTime(offer.rejectedAt)}
                                                </p>
                                              )}
                                              {!offer.rejector && (
                                                <p className="text-amber-600 dark:text-amber-400 text-xs">
                                                  Cancelada
                                                  {offer.rejectedAt
                                                    ? ` em ${formatDateTime(offer.rejectedAt)}`
                                                    : ""}
                                                </p>
                                              )}
                                            </div>
                                          </CollapsibleContent>
                                        </Collapsible>
                                      </div>
                                    )}

                                    {/* Footer: creator + actions */}
                                    <div className="px-4 py-2.5 bg-muted/20 border-t flex items-center justify-between">
                                      <p className="text-xs text-muted-foreground">
                                        {offer.creator
                                          ? `Criada por ${offer.creator.fullName}`
                                          : ""}
                                      </p>
                                      <div className="flex items-center gap-1">
                                        {offer.acceptanceToken && (
                                          <>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 px-2 text-xs"
                                              onClick={() => {
                                                navigator.clipboard.writeText(
                                                  `${window.location.origin}/offer/${offer.acceptanceToken}`,
                                                );
                                                toast({
                                                  title: "Link copiado!",
                                                  description:
                                                    "Link da oferta copiado para a área de transferência.",
                                                });
                                              }}
                                            >
                                              <Copy className="w-3 h-3 mr-1" />
                                              Copiar Link
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 px-2 text-xs"
                                              onClick={() =>
                                                window.open(
                                                  `/offer-preview/${offer.previewToken}`,
                                                  "_blank",
                                                )
                                              }
                                            >
                                              <Eye className="w-3 h-3 mr-1" />
                                              Preview
                                            </Button>
                                          </>
                                        )}
                                        {offer.status === "approved" && (
                                          <>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                                              onClick={() => setSendConfirmOfferId(offer.id)}
                                              disabled={sendOfferMutation.isPending}
                                            >
                                              <Send className="w-3 h-3 mr-1" />
                                              {sendOfferMutation.isPending
                                                ? "Enviando..."
                                                : "Enviar ao Candidato"}
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                                              onClick={() =>
                                                markOfferAsSentMutation.mutate(offer.id)
                                              }
                                              disabled={markOfferAsSentMutation.isPending}
                                            >
                                              <CheckCheck className="w-3 h-3 mr-1" />
                                              {markOfferAsSentMutation.isPending
                                                ? "Salvando..."
                                                : "Marcar como Enviada"}
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                Nenhuma oferta registrada
                              </p>
                            )}
                            {canManage &&
                              (() => {
                                const latestOffer = details.offers?.[0];
                                const canCreateOffer =
                                  (!latestOffer ||
                                    latestOffer.status === "rejected" ||
                                    latestOffer.status === "declined" ||
                                    latestOffer.status === "cancelled") &&
                                  details.status !== "hired" &&
                                  details.status !== "rejected";
                                return (
                                  <div className="flex gap-2 flex-wrap mt-3">
                                    {canCreateOffer && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setIsCreateOfferOpen(true)}
                                      >
                                        <Plus className="w-3 h-3 mr-1" />
                                        Criar Nova Oferta
                                      </Button>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setIsSimulateOfferOpen(true)}
                                    >
                                      <FlaskConical className="w-3 h-3 mr-1" />
                                      Simular Oferta
                                    </Button>
                                  </div>
                                );
                              })()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Dossiê — aparece quando: status in [dossier, offer, hired] OU dossierDecision existe */}
                    {(["dossier", "offer", "hired"].includes(details.status) ||
                      (details.status === "rejected" && details.dossierDecision)) && (
                      <div id="section-dossie" className="border-b border-border/40 px-7">
                        <SectionHeader
                          id="dossie"
                          icon={<ClipboardEdit className="w-3.5 h-3.5" />}
                          title="Dossiê"
                        />
                        {!collapsedSections.has("dossie") && (
                          <div className="pb-4">
                            <a
                              href={`/dossier/${details.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block rounded-lg p-3 text-sm transition-colors bg-muted/50 border border-border hover:bg-muted"
                              data-testid="link-dossier-section"
                            >
                              {details.dossierDecision && (
                                <div data-testid="dossier-decision-result">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {details.dossierDecision === "approved" ? (
                                        <Badge
                                          variant="outline"
                                          className="border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400 hover:bg-stone-50"
                                          data-testid="badge-dossier-approved"
                                        >
                                          Aprovado
                                        </Badge>
                                      ) : (
                                        <Badge
                                          variant="outline"
                                          className="border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400 hover:bg-stone-50"
                                          data-testid="badge-dossier-rejected"
                                        >
                                          Rejeitado
                                        </Badge>
                                      )}
                                      {details.dossierDecidedByEmployee?.fullName && (
                                        <span
                                          className="text-xs text-muted-foreground"
                                          data-testid="text-dossier-decided-by"
                                        >
                                          por {details.dossierDecidedByEmployee.fullName}
                                        </span>
                                      )}
                                    </div>
                                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                                  </div>
                                  {details.dossierDecision === "rejected" &&
                                    details.dossierDecisionReason && (
                                      <p
                                        className="text-sm text-muted-foreground mt-2"
                                        data-testid="text-dossier-rejection-reason"
                                      >
                                        <strong>Justificativa:</strong>{" "}
                                        {details.dossierDecisionReason}
                                      </p>
                                    )}
                                </div>
                              )}
                              {!details.dossierDecision && (
                                <div className="flex items-center gap-2 text-blue-600">
                                  <FileText className="w-4 h-4" />
                                  Ver Dossiê Completo
                                  <ExternalLink className="w-3.5 h-3.5 ml-auto" />
                                </div>
                              )}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Reference Checks — aparece quando: status in [reference_checks, dossier, offer, hired] OU existem RCs */}
                    {(["reference_checks", "dossier", "offer", "hired"].includes(details.status) ||
                      (details.referenceChecks?.length ?? 0) > 0) && (
                      <div id="section-reference-checks" className="border-b border-border/40 px-7">
                        <SectionHeader
                          id="reference-checks"
                          icon={<Users className="w-3.5 h-3.5" />}
                          title="Reference Checks"
                        />
                        {!collapsedSections.has("reference-checks") && (
                          <div className="pb-4 space-y-3">
                            {(details.referenceChecks?.length ?? 0) > 0 ? (
                              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                                {details.referenceChecks!.map((rc) => {
                                  const hasAnswers =
                                    rc.answers &&
                                    rc.answers.length > 0 &&
                                    rc.answers.every((a) => a.answer.trim().length > 0);
                                  return (
                                    <div
                                      key={rc.id}
                                      className="flex-shrink-0 w-52 rounded-lg border border-border bg-card p-3 flex flex-col gap-2"
                                    >
                                      <div className="flex items-start justify-between gap-1">
                                        <p className="text-sm font-medium leading-tight line-clamp-2">
                                          {rc.referrerName}
                                        </p>
                                        <div className="flex gap-1 flex-shrink-0">
                                          <button
                                            type="button"
                                            onClick={() => setViewingRc(rc)}
                                            className="text-muted-foreground hover:text-foreground transition-colors"
                                          >
                                            <Eye className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setEditingRc(rc);
                                              setRcContactForm({
                                                referrerName: rc.referrerName,
                                                referrerLinkedin: rc.referrerLinkedin || "",
                                                referrerPhone: rc.referrerPhone || "",
                                              });
                                              const savedAnswers = rc.answers || [];
                                              const merged = DEFAULT_REFERENCE_QUESTIONS.map(
                                                (q) => ({
                                                  question: q,
                                                  answer:
                                                    savedAnswers.find((a) => a.question === q)
                                                      ?.answer || "",
                                                }),
                                              );
                                              const extras = savedAnswers.filter(
                                                (a) =>
                                                  !DEFAULT_REFERENCE_QUESTIONS.includes(a.question),
                                              );
                                              setRcAnswersForm([...merged, ...extras]);
                                              setRcDrawerTab("contato");
                                              setRcDrawerOpen(true);
                                            }}
                                            className="text-muted-foreground hover:text-foreground transition-colors"
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => setDeletingRcId(rc.id)}
                                            className="text-muted-foreground hover:text-destructive transition-colors"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                      <Badge
                                        variant="outline"
                                        className="border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400 w-fit text-xs"
                                      >
                                        {hasAnswers ? "Respondido" : "Pendente"}
                                      </Badge>
                                      {rc.referrerLinkedin && (
                                        <a
                                          href={rc.referrerLinkedin}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 truncate"
                                        >
                                          <Linkedin className="w-3 h-3 flex-shrink-0" />
                                          <span className="truncate">LinkedIn</span>
                                        </a>
                                      )}
                                      {rc.referrerPhone && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                          <Phone className="w-3 h-3 flex-shrink-0" />
                                          <span className="truncate">{rc.referrerPhone}</span>
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                Nenhum reference check ainda.
                              </p>
                            )}
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingRc(null);
                                  setRcContactForm({
                                    referrerName: "",
                                    referrerLinkedin: "",
                                    referrerPhone: "",
                                  });
                                  setRcAnswersForm(
                                    DEFAULT_REFERENCE_QUESTIONS.map((q) => ({
                                      question: q,
                                      answer: "",
                                    })),
                                  );
                                  setRcDrawerTab("contato");
                                  setRcDrawerOpen(true);
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Adicionar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRcTemplateOpen(true)}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Ver template
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const text = DEFAULT_REFERENCE_QUESTIONS.map(
                                    (q, i) => `${i + 1}. ${q}`,
                                  ).join("\n\n");
                                  navigator.clipboard.writeText(text);
                                  toast({ title: "Template copiado!" });
                                }}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copiar template
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notas de entrevistadores — aparece sempre */}
                    <div id="section-notas" className="border-b border-border/40 px-7">
                      <SectionHeader
                        id="notas"
                        icon={<StickyNote className="w-3.5 h-3.5" />}
                        title="Notas de entrevistadores"
                        tooltip="Registre aqui observações importantes para outros entrevistadores consultarem durante o processo seletivo"
                      />
                      {!collapsedSections.has("notas") && (
                        <div className="pb-4 space-y-3" data-testid="executive-summary">
                          {(() => {
                            const sortedInterviews = details.interviews
                              .filter((i) => i.notesForNextInterviewer)
                              .sort(
                                (a, b) => (a.stage?.stageOrder || 0) - (b.stage?.stageOrder || 0),
                              );
                            const interviewReplies = (notes || []).filter(
                              (n) => n.type === "interview_reply",
                            );
                            const _interviewFreeNotes = (notes || []).filter(
                              (n) => n.type === "interview_note",
                            );

                            if (sortedInterviews.length === 0) {
                              return (
                                <p className="text-sm text-muted-foreground">
                                  Nenhuma nota registrada ainda.
                                </p>
                              );
                            }

                            return sortedInterviews.map((interview) => {
                              const byline = `${interview.stage?.stageName || "Entrevista"} — ${interview.groupMemberNames?.join(" e ") || interview.interviewer?.fullName || "Entrevistador"}`;
                              const replies = interviewReplies.filter(
                                (n) => n.interviewId === interview.id,
                              );
                              const isReplying = replyingToInterviewId === interview.id;

                              return (
                                <div key={interview.id}>
                                  {/* Nota original do entrevistador */}
                                  <div className="bg-muted/50 rounded-lg p-3">
                                    <p className="text-xs font-medium text-muted-foreground mb-1">
                                      {byline}
                                    </p>
                                    <p className="text-sm whitespace-pre-wrap">
                                      {interview.notesForNextInterviewer}
                                    </p>
                                  </div>

                                  {/* Replies existentes */}
                                  {replies.length > 0 && (
                                    <div className="ml-6 mt-1 space-y-1 border-l border-border/50 pl-3">
                                      {replies.map((reply) => (
                                        <div key={reply.id} className="py-1.5">
                                          <div className="flex items-center justify-between mb-0.5">
                                            <span className="text-xs font-medium">
                                              {reply.author?.fullName || "Autor"}
                                            </span>
                                            <div className="flex items-center gap-1">
                                              <span className="text-[10px] text-muted-foreground">
                                                {formatDateTime(reply.createdAt)}
                                              </span>
                                              {editingNoteId !== reply.id && (
                                                <>
                                                  <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-5 w-5"
                                                    onClick={() => {
                                                      setEditingNoteId(reply.id);
                                                      setEditingNoteContent(reply.content);
                                                    }}
                                                    data-testid={`button-edit-note-${reply.id}`}
                                                  >
                                                    <Pencil className="w-3 h-3" />
                                                  </Button>
                                                  <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-5 w-5 text-destructive hover:text-destructive"
                                                    onClick={() =>
                                                      deleteNoteMutation.mutate(reply.id)
                                                    }
                                                    disabled={deleteNoteMutation.isPending}
                                                    data-testid={`button-delete-note-${reply.id}`}
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </Button>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                          {editingNoteId === reply.id ? (
                                            <div className="space-y-1">
                                              <Textarea
                                                value={editingNoteContent}
                                                onChange={(e) =>
                                                  setEditingNoteContent(e.target.value)
                                                }
                                                className="text-sm"
                                                rows={2}
                                                data-testid={`input-edit-note-${reply.id}`}
                                              />
                                              <div className="flex gap-1 justify-end">
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-6 w-6"
                                                  onClick={() => {
                                                    setEditingNoteId(null);
                                                    setEditingNoteContent("");
                                                  }}
                                                  data-testid={`button-cancel-edit-note-${reply.id}`}
                                                >
                                                  <X className="w-3 h-3" />
                                                </Button>
                                                <Button
                                                  size="icon"
                                                  className="h-6 w-6"
                                                  disabled={
                                                    !editingNoteContent.trim() ||
                                                    updateNoteMutation.isPending
                                                  }
                                                  onClick={() =>
                                                    updateNoteMutation.mutate({
                                                      noteId: reply.id,
                                                      content: editingNoteContent.trim(),
                                                    })
                                                  }
                                                  data-testid={`button-save-edit-note-${reply.id}`}
                                                >
                                                  <Check className="w-3 h-3" />
                                                </Button>
                                              </div>
                                            </div>
                                          ) : (
                                            <>
                                              <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                                                {reply.content}
                                              </p>
                                              {renderAttachments(reply.attachments)}
                                            </>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Input de reply / botão para abrir */}
                                  {isReplying ? (
                                    <div className="ml-6 mt-1 border-l border-border/50 pl-3 space-y-1">
                                      {renderPendingAttachments(`reply_${interview.id}`)}
                                      <div className="flex gap-2 items-end">
                                        <Textarea
                                          placeholder="Adicionar comentário..."
                                          value={replyContent[interview.id] || ""}
                                          onChange={(e) =>
                                            setReplyContent((prev) => ({
                                              ...prev,
                                              [interview.id]: e.target.value,
                                            }))
                                          }
                                          onPaste={(e) => handlePaste(`reply_${interview.id}`, e)}
                                          className="text-sm flex-1 min-w-0"
                                          rows={2}
                                          autoFocus
                                        />
                                        {renderAttachButton(`reply_${interview.id}`)}
                                        <div className="flex flex-col gap-1 shrink-0">
                                          <Button
                                            type="button"
                                            size="icon"
                                            className="h-7 w-7"
                                            disabled={
                                              (!replyContent[interview.id]?.trim() &&
                                                !pendingAttachments[`reply_${interview.id}`]
                                                  ?.length) ||
                                              createReplyMutation.isPending
                                            }
                                            onClick={() =>
                                              createReplyMutation.mutate({
                                                interviewId: interview.id,
                                                content: replyContent[interview.id]?.trim() || " ",
                                                attachments: getAttachmentsForKey(
                                                  `reply_${interview.id}`,
                                                ),
                                              })
                                            }
                                          >
                                            <Send className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => setReplyingToInterviewId(null)}
                                          >
                                            <X className="w-3.5 h-3.5" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setReplyingToInterviewId(interview.id)}
                                      className="ml-6 mt-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                      + Comentar
                                    </button>
                                  )}
                                </div>
                              );
                            });
                          })()}

                          {/* Comentários livres na seção de notas de entrevistadores */}
                          {(() => {
                            const interviewFreeNotes = (notes || []).filter(
                              (n) => n.type === "interview_note",
                            );
                            return (
                              interviewFreeNotes.length > 0 && (
                                <div className="space-y-1 pt-1">
                                  {interviewFreeNotes.map((note) => (
                                    <div
                                      key={note.id}
                                      className="py-1.5"
                                      data-testid={`note-${note.id}`}
                                    >
                                      <div className="flex items-center justify-between mb-0.5">
                                        <span className="text-xs font-medium">
                                          {note.author?.fullName || "Autor"}
                                        </span>
                                        <div className="flex items-center gap-1">
                                          <span className="text-[10px] text-muted-foreground">
                                            {formatDateTime(note.createdAt)}
                                          </span>
                                          {editingNoteId !== note.id && (
                                            <>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-5 w-5"
                                                onClick={() => {
                                                  setEditingNoteId(note.id);
                                                  setEditingNoteContent(note.content);
                                                }}
                                                data-testid={`button-edit-note-${note.id}`}
                                              >
                                                <Pencil className="w-3 h-3" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-5 w-5 text-destructive hover:text-destructive"
                                                onClick={() => deleteNoteMutation.mutate(note.id)}
                                                disabled={deleteNoteMutation.isPending}
                                                data-testid={`button-delete-note-${note.id}`}
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                      {editingNoteId === note.id ? (
                                        <div className="space-y-1">
                                          <Textarea
                                            value={editingNoteContent}
                                            onChange={(e) => setEditingNoteContent(e.target.value)}
                                            className="text-sm"
                                            rows={2}
                                            data-testid={`input-edit-note-${note.id}`}
                                          />
                                          <div className="flex gap-1 justify-end">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-6 w-6"
                                              onClick={() => {
                                                setEditingNoteId(null);
                                                setEditingNoteContent("");
                                              }}
                                              data-testid={`button-cancel-edit-note-${note.id}`}
                                            >
                                              <X className="w-3 h-3" />
                                            </Button>
                                            <Button
                                              size="icon"
                                              className="h-6 w-6"
                                              disabled={
                                                !editingNoteContent.trim() ||
                                                updateNoteMutation.isPending
                                              }
                                              onClick={() =>
                                                updateNoteMutation.mutate({
                                                  noteId: note.id,
                                                  content: editingNoteContent.trim(),
                                                })
                                              }
                                              data-testid={`button-save-edit-note-${note.id}`}
                                            >
                                              <Check className="w-3 h-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                                            {note.content}
                                          </p>
                                          {renderAttachments(note.attachments)}
                                        </>
                                      )}

                                      {/* Replies da interview_note */}
                                      {(() => {
                                        const noteReplies = (notes || []).filter(
                                          (n) =>
                                            n.type === "interview_note_reply" &&
                                            n.parentId === note.id,
                                        );
                                        const isReplyingToNote = replyingToNoteId === note.id;
                                        return (
                                          <div className="mt-1">
                                            {noteReplies.length > 0 && (
                                              <div className="ml-4 space-y-1 border-l border-border/50 pl-3">
                                                {noteReplies.map((reply) => (
                                                  <div key={reply.id} className="py-1">
                                                    <div className="flex items-center justify-between mb-0.5">
                                                      <span className="text-xs font-medium">
                                                        {reply.author?.fullName || "Autor"}
                                                      </span>
                                                      <div className="flex items-center gap-1">
                                                        <span className="text-[10px] text-muted-foreground">
                                                          {formatDateTime(reply.createdAt)}
                                                        </span>
                                                        <Button
                                                          variant="ghost"
                                                          size="icon"
                                                          className="h-5 w-5 text-destructive hover:text-destructive"
                                                          onClick={() =>
                                                            deleteNoteMutation.mutate(reply.id)
                                                          }
                                                          disabled={deleteNoteMutation.isPending}
                                                        >
                                                          <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                      </div>
                                                    </div>
                                                    <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                                                      {reply.content}
                                                    </p>
                                                    {renderAttachments(reply.attachments)}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            {isReplyingToNote ? (
                                              <div className="ml-4 mt-1 border-l border-border/50 pl-3 space-y-1">
                                                {renderPendingAttachments(`noteReply_${note.id}`)}
                                                <div className="flex gap-2 items-end">
                                                  <Textarea
                                                    placeholder="Adicionar comentário..."
                                                    value={noteReplyContent[note.id] || ""}
                                                    onChange={(e) =>
                                                      setNoteReplyContent((prev) => ({
                                                        ...prev,
                                                        [note.id]: e.target.value,
                                                      }))
                                                    }
                                                    onPaste={(e) =>
                                                      handlePaste(`noteReply_${note.id}`, e)
                                                    }
                                                    className="text-sm flex-1 min-w-0"
                                                    rows={2}
                                                    autoFocus
                                                  />
                                                  {renderAttachButton(`noteReply_${note.id}`)}
                                                  <div className="flex flex-col gap-1 shrink-0">
                                                    <Button
                                                      type="button"
                                                      size="icon"
                                                      className="h-7 w-7"
                                                      disabled={
                                                        (!noteReplyContent[note.id]?.trim() &&
                                                          !pendingAttachments[
                                                            `noteReply_${note.id}`
                                                          ]?.length) ||
                                                        createNoteReplyMutation.isPending
                                                      }
                                                      onClick={() =>
                                                        createNoteReplyMutation.mutate({
                                                          parentId: note.id,
                                                          content:
                                                            noteReplyContent[note.id]?.trim() ||
                                                            " ",
                                                          attachments: getAttachmentsForKey(
                                                            `noteReply_${note.id}`,
                                                          ),
                                                        })
                                                      }
                                                    >
                                                      <Send className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button
                                                      variant="ghost"
                                                      size="icon"
                                                      className="h-7 w-7"
                                                      onClick={() => setReplyingToNoteId(null)}
                                                    >
                                                      <X className="w-3.5 h-3.5" />
                                                    </Button>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() => setReplyingToNoteId(note.id)}
                                                className="ml-4 mt-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                                              >
                                                + Comentar
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  ))}
                                </div>
                              )
                            );
                          })()}

                          {/* Input novo comentário livre na seção */}
                          <div className="space-y-1 pt-2 border-t border-border/40">
                            {renderPendingAttachments("interviewNote")}
                            <div className="flex gap-2 items-end">
                              <Textarea
                                placeholder="Adicionar comentário..."
                                value={newInterviewNoteContent}
                                onChange={(e) => setNewInterviewNoteContent(e.target.value)}
                                onPaste={(e) => handlePaste("interviewNote", e)}
                                className="text-sm flex-1 min-w-0"
                                rows={2}
                              />
                              {renderAttachButton("interviewNote")}
                              <Button
                                type="button"
                                size="icon"
                                className="self-end shrink-0"
                                disabled={
                                  (!newInterviewNoteContent.trim() &&
                                    !pendingAttachments.interviewNote?.length) ||
                                  createInterviewNoteMutation.isPending
                                }
                                onClick={() =>
                                  createInterviewNoteMutation.mutate({
                                    content: newInterviewNoteContent.trim() || " ",
                                    attachments: getAttachmentsForKey("interviewNote"),
                                  })
                                }
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Entrevistas */}
                    <div id="section-entrevistas" className="border-b border-border/40 px-7">
                      <SectionHeader
                        id="entrevistas"
                        icon={<MessageSquare className="w-3.5 h-3.5" />}
                        title="Entrevistas"
                        count={details.interviews.length}
                      />
                      {!collapsedSections.has("entrevistas") && (
                        <div className="pb-4">
                          {details.interviewStages && details.interviewStages.length > 0 ? (
                            <div className="ml-1" data-testid="interview-stepper">
                              {details.interviewStages
                                .filter(
                                  (s) =>
                                    !s.hidden &&
                                    (!s.archivedAt ||
                                      details.interviews.some((i) => i.interviewStageId === s.id)),
                                )
                                .sort((a, b) => a.stageOrder - b.stageOrder)
                                .map((stage, idx, arr) => {
                                  const stageInterviews = details.interviews
                                    .filter((i) => i.interviewStageId === stage.id)
                                    .sort(
                                      (a, b) =>
                                        new Date(a.createdAt || 0).getTime() -
                                        new Date(b.createdAt || 0).getTime(),
                                    );
                                  const isCurrent = details.currentInterviewStageId === stage.id;
                                  const isCompleted =
                                    stageInterviews.length > 0 &&
                                    stageInterviews.every((i) => i.contextStatus === "Finalizado");
                                  const isLast = idx === arr.length - 1;

                                  return (
                                    <div
                                      key={stage.id}
                                      className="flex gap-3"
                                      data-testid={`stepper-stage-${stage.id}`}
                                    >
                                      <div className="flex flex-col items-center">
                                        <div
                                          className={`flex items-center justify-center w-6 h-6 rounded-full shrink-0 mt-0.5 ${
                                            isCompleted
                                              ? "bg-primary text-primary-foreground"
                                              : isCurrent
                                                ? "border-2 border-primary bg-background"
                                                : "border-2 border-muted-foreground/30 bg-background"
                                          }`}
                                        >
                                          {isCompleted ? (
                                            <Check className="w-3.5 h-3.5" />
                                          ) : isCurrent ? (
                                            <div className="w-2 h-2 rounded-full bg-primary" />
                                          ) : null}
                                        </div>
                                        {!isLast && (
                                          <div
                                            className={`w-0.5 flex-1 min-h-[24px] ${
                                              isCompleted ? "bg-primary" : "bg-muted-foreground/20"
                                            }`}
                                          />
                                        )}
                                      </div>
                                      <div className={`flex-1 min-w-0 ${isLast ? "pb-0" : "pb-3"}`}>
                                        {stageInterviews.length > 0 ? (
                                          <div className="space-y-2">
                                            {stageInterviews.map((interview, interviewIdx) => (
                                              <Collapsible
                                                key={interview.id}
                                                open={expandedInterviews.has(interview.id)}
                                                onOpenChange={() => toggleInterview(interview.id)}
                                              >
                                                <div
                                                  className="rounded-lg bg-stone-50 dark:bg-white/[0.03] border border-border/60"
                                                  data-testid={`interview-card-${interview.id}`}
                                                >
                                                  <CollapsibleTrigger
                                                    className="w-full text-left p-3"
                                                    data-testid={`button-interview-toggle-${interview.id}`}
                                                  >
                                                    <div className="flex items-center justify-between gap-2">
                                                      <div className="flex items-center gap-2 min-w-0">
                                                        <span
                                                          className={`text-sm font-semibold truncate ${isCurrent ? "text-foreground" : "text-foreground/80"}`}
                                                        >
                                                          {stage.stageName}
                                                          {stageInterviews.length > 1
                                                            ? ` #${interviewIdx + 1}`
                                                            : ""}
                                                        </span>
                                                        {stage.archivedAt && (
                                                          <Badge
                                                            variant="outline"
                                                            className="text-amber-500 border-amber-500/30 text-xs ml-1"
                                                          >
                                                            Arquivada
                                                          </Badge>
                                                        )}
                                                        <span className="text-xs text-muted-foreground truncate">
                                                          com{" "}
                                                          {interview.groupMemberNames &&
                                                          interview.groupMemberNames.length > 0
                                                            ? interview.groupMemberNames.join(" e ")
                                                            : interview.interviewer?.fullName ||
                                                              "Entrevistador"}
                                                        </span>
                                                        <div className="flex items-center gap-0.5 shrink-0">
                                                          {interview.contextStatus ===
                                                            "Não comunicado" &&
                                                            (interview.meetingLink ||
                                                              interview.interviewer
                                                                ?.schedulingLink) && (
                                                              <button
                                                                type="button"
                                                                data-testid={`button-copy-scheduling-link-${interview.id}`}
                                                                className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                                                                title="Copiar link de agendamento"
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  const link =
                                                                    interview.meetingLink ||
                                                                    interview.interviewer
                                                                      ?.schedulingLink ||
                                                                    "";
                                                                  navigator.clipboard.writeText(
                                                                    link,
                                                                  );
                                                                  toast({
                                                                    title: "Link copiado!",
                                                                    description:
                                                                      "Link de agendamento copiado para a área de transferência.",
                                                                  });
                                                                }}
                                                              >
                                                                <Copy className="w-3.5 h-3.5" />
                                                              </button>
                                                            )}
                                                          {[
                                                            "Não comunicado",
                                                            "Aguardando Agendamento",
                                                            "Agendado",
                                                          ].includes(
                                                            interview.contextStatus || "",
                                                          ) &&
                                                            interview.status !== "finished" &&
                                                            interview.interviewStageId ===
                                                              details.currentInterviewStageId &&
                                                            canManage && (
                                                              <button
                                                                type="button"
                                                                data-testid={`button-change-interviewer-${interview.id}`}
                                                                className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                                                                title="Trocar entrevistador"
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  setChangeInterviewerForId(
                                                                    interview.id,
                                                                  );
                                                                }}
                                                              >
                                                                <UserCog className="w-3.5 h-3.5" />
                                                              </button>
                                                            )}
                                                          {interview.contextStatus === "Agendado" &&
                                                            canManage && (
                                                              <button
                                                                type="button"
                                                                data-testid={`button-reschedule-interview-${interview.id}`}
                                                                className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                                                                title="Reagendar entrevista"
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  reschedule.openReschedule(
                                                                    interview.id,
                                                                    !!interview.interviewerGroupId,
                                                                  );
                                                                }}
                                                              >
                                                                <RefreshCw className="w-3.5 h-3.5" />
                                                              </button>
                                                            )}
                                                          {[
                                                            "Não comunicado",
                                                            "Aguardando Agendamento",
                                                            "Agendado",
                                                          ].includes(
                                                            interview.contextStatus || "",
                                                          ) &&
                                                            canManage && (
                                                              <button
                                                                type="button"
                                                                data-testid={`button-delete-interview-${interview.id}`}
                                                                className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-destructive transition-colors"
                                                                title="Excluir entrevista"
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  const code = Math.random()
                                                                    .toString(36)
                                                                    .substring(2, 6)
                                                                    .toUpperCase();
                                                                  setDeleteExpectedCode(code);
                                                                  setDeleteConfirmCode("");
                                                                  setDeleteInterviewId(
                                                                    interview.id,
                                                                  );
                                                                }}
                                                              >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                              </button>
                                                            )}
                                                          {canManage && (
                                                            <DropdownMenu>
                                                              <DropdownMenuTrigger asChild>
                                                                <button
                                                                  type="button"
                                                                  className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                                                                  onClick={(e) =>
                                                                    e.stopPropagation()
                                                                  }
                                                                >
                                                                  <MoreVertical className="w-3.5 h-3.5" />
                                                                </button>
                                                              </DropdownMenuTrigger>
                                                              <DropdownMenuContent align="end">
                                                                <DropdownMenuItem
                                                                  onClick={() =>
                                                                    setMoveInterviewId(interview.id)
                                                                  }
                                                                >
                                                                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                                                                  Mover para outra etapa
                                                                </DropdownMenuItem>
                                                              </DropdownMenuContent>
                                                            </DropdownMenu>
                                                          )}
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-2 shrink-0">
                                                        {interview.contextStatus === "Finalizado" &&
                                                          interview.recommendation && (
                                                            <span className="text-xs font-medium text-stone-600 dark:text-stone-400">
                                                              {interview.recommendation ===
                                                              "advance"
                                                                ? "Recomenda"
                                                                : "Não recomenda"}
                                                            </span>
                                                          )}
                                                        {interview.contextStatus ===
                                                          "Não comunicado" && (
                                                          <button
                                                            type="button"
                                                            className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                                                            title="Enviar email de convite"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setSendEmailInterviewId(interview.id);
                                                            }}
                                                            data-testid={`button-send-interview-email-${interview.id}`}
                                                          >
                                                            <Send className="w-3.5 h-3.5" />
                                                          </button>
                                                        )}
                                                        {(() => {
                                                          if (
                                                            interview.contextStatus !== "Finalizado"
                                                          )
                                                            return null;
                                                          // Only show for the actual interviewer or group members
                                                          const isInterviewer =
                                                            interview.interviewer?.id ===
                                                              employee?.id ||
                                                            (employee?.id
                                                              ? (interview.groupMemberIds?.includes(
                                                                  employee.id,
                                                                ) ?? false)
                                                              : false);
                                                          if (!isInterviewer) return null;
                                                          // Hide if any evaluation already exists
                                                          if (interview.recommendation) return null;
                                                          return (
                                                            <Button
                                                              size="sm"
                                                              variant="outline"
                                                              className="h-6 text-xs px-2"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                openFeedbackDialog(interview);
                                                              }}
                                                              data-testid={`button-header-fill-feedback-${interview.id}`}
                                                            >
                                                              <ClipboardEdit className="w-3 h-3 mr-1" />
                                                              Avaliar
                                                            </Button>
                                                          );
                                                        })()}
                                                        {getInterviewStatusBadge(interview)}
                                                        <ChevronDown
                                                          className={`w-4 h-4 text-muted-foreground transition-transform ${
                                                            expandedInterviews.has(interview.id)
                                                              ? "rotate-180"
                                                              : ""
                                                          }`}
                                                        />
                                                      </div>
                                                    </div>
                                                    {/* Contextual second line per status */}
                                                    {interview.contextStatus ===
                                                      "Aguardando Agendamento" &&
                                                      interview.candidateNotifiedAt && (
                                                        <div className="flex items-center gap-1.5 mt-1.5">
                                                          <Badge
                                                            variant="outline"
                                                            className="text-[10px] font-medium border-emerald-500 text-emerald-600 dark:text-emerald-400 gap-0.5"
                                                          >
                                                            <CheckCircle className="w-2.5 h-2.5" />
                                                            Comunicado em{" "}
                                                            {formatDate(
                                                              interview.candidateNotifiedAt,
                                                            )}
                                                          </Badge>
                                                        </div>
                                                      )}
                                                    {interview.contextStatus === "Agendado" &&
                                                      interview.scheduledAt && (
                                                        <div className="text-xs text-muted-foreground mt-1">
                                                          Agendado para:{" "}
                                                          {formatDateTime(interview.scheduledAt)}
                                                        </div>
                                                      )}
                                                    {[
                                                      "Aguardando Avaliação",
                                                      "Finalizado",
                                                    ].includes(interview.contextStatus || "") &&
                                                      interview.scheduledAt && (
                                                        <div className="text-xs text-muted-foreground mt-1">
                                                          Realizada em:{" "}
                                                          {formatDateTime(interview.scheduledAt)}
                                                        </div>
                                                      )}
                                                  </CollapsibleTrigger>
                                                  <CollapsibleContent>
                                                    <div className="px-3 pb-3">
                                                      {interview.stage?.materials && (
                                                        <div
                                                          className="pt-3 border-t border-border/60"
                                                          data-testid={`interview-materials-${interview.id}`}
                                                        >
                                                          <p className="text-xs font-medium text-muted-foreground mb-1">
                                                            Material de Apoio
                                                          </p>
                                                          <p className="text-sm whitespace-pre-wrap text-foreground/80">
                                                            {interview.stage.materials}
                                                          </p>
                                                        </div>
                                                      )}
                                                      {interview.feedbackRedacted &&
                                                      interview.completedAt &&
                                                      !(
                                                        interview.feedbacks &&
                                                        interview.feedbacks.length > 0
                                                      ) &&
                                                      !interview.gcaVerdict &&
                                                      !interview.roleKnowledgeVerdict &&
                                                      !interview.compWayVerdict &&
                                                      !interview.recommendation &&
                                                      !interview.gcaNotes &&
                                                      !interview.roleKnowledgeNotes &&
                                                      !interview.compWayNotes &&
                                                      !interview.customCriteriaScores?.length &&
                                                      !interview.contextSignals &&
                                                      !interview.generalComments ? (
                                                        <div className="pt-3 border-t border-border/60">
                                                          <p
                                                            className="text-sm text-muted-foreground italic"
                                                            data-testid={`text-feedback-redacted-${interview.id}`}
                                                          >
                                                            Feedback oculto durante fase de
                                                            entrevistas
                                                          </p>
                                                        </div>
                                                      ) : interview.feedbacks &&
                                                        interview.feedbacks.length > 0 ? (
                                                        <div className="pt-3 border-t border-border/60 space-y-3">
                                                          {interview.feedbacks.map((fb) => (
                                                            <div
                                                              key={fb.id}
                                                              className="rounded-lg bg-background border border-border/50 p-3 space-y-2.5"
                                                              data-testid={`group-feedback-${fb.id}`}
                                                            >
                                                              <div className="flex items-center justify-between">
                                                                <p className="text-sm font-semibold text-foreground">
                                                                  {fb.employeeName}
                                                                </p>
                                                                {fb.recommendation && (
                                                                  <Badge
                                                                    variant="outline"
                                                                    className="text-[10px] border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400"
                                                                  >
                                                                    {fb.recommendation === "advance"
                                                                      ? "Aprovar"
                                                                      : "Recusar"}
                                                                  </Badge>
                                                                )}
                                                              </div>
                                                              {(fb.gcaVerdict || fb.gcaNotes) && (
                                                                <div>
                                                                  <div className="flex items-center justify-between mb-1">
                                                                    <p className="text-xs font-medium text-muted-foreground">
                                                                      GCA
                                                                    </p>
                                                                    {fb.gcaVerdict && (
                                                                      <Badge
                                                                        variant="outline"
                                                                        className="text-[10px] border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400"
                                                                      >
                                                                        {fb.gcaVerdict === "YES"
                                                                          ? "Sim"
                                                                          : fb.gcaVerdict === "NO"
                                                                            ? "Não"
                                                                            : "N/A"}
                                                                      </Badge>
                                                                    )}
                                                                  </div>
                                                                  {fb.gcaNotes && (
                                                                    <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-2.5 py-2 whitespace-pre-wrap">
                                                                      {fb.gcaNotes}
                                                                    </p>
                                                                  )}
                                                                </div>
                                                              )}
                                                              {(fb.roleKnowledgeVerdict ||
                                                                fb.roleKnowledgeNotes) && (
                                                                <div>
                                                                  <div className="flex items-center justify-between mb-1">
                                                                    <p className="text-xs font-medium text-muted-foreground">
                                                                      Role-Related Knowledge
                                                                    </p>
                                                                    {fb.roleKnowledgeVerdict && (
                                                                      <Badge
                                                                        variant="outline"
                                                                        className="text-[10px] border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400"
                                                                      >
                                                                        {fb.roleKnowledgeVerdict ===
                                                                        "YES"
                                                                          ? "Sim"
                                                                          : fb.roleKnowledgeVerdict ===
                                                                              "NO"
                                                                            ? "Não"
                                                                            : "N/A"}
                                                                      </Badge>
                                                                    )}
                                                                  </div>
                                                                  {fb.roleKnowledgeNotes && (
                                                                    <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-2.5 py-2 whitespace-pre-wrap">
                                                                      {fb.roleKnowledgeNotes}
                                                                    </p>
                                                                  )}
                                                                </div>
                                                              )}
                                                              {(fb.compWayVerdict ||
                                                                fb.compWayNotes) && (
                                                                <div>
                                                                  <div className="flex items-center justify-between mb-1">
                                                                    <p className="text-xs font-medium text-muted-foreground">
                                                                      COMP Way
                                                                    </p>
                                                                    {fb.compWayVerdict && (
                                                                      <Badge
                                                                        variant="outline"
                                                                        className="text-[10px] border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400"
                                                                      >
                                                                        {fb.compWayVerdict === "YES"
                                                                          ? "Sim"
                                                                          : fb.compWayVerdict ===
                                                                              "NO"
                                                                            ? "Não"
                                                                            : "N/A"}
                                                                      </Badge>
                                                                    )}
                                                                  </div>
                                                                  {fb.compWayNotes && (
                                                                    <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-2.5 py-2 whitespace-pre-wrap">
                                                                      {fb.compWayNotes}
                                                                    </p>
                                                                  )}
                                                                </div>
                                                              )}
                                                              {!!fb.customCriteriaScores
                                                                ?.length && (
                                                                <CustomCriteriaDisplay
                                                                  scores={fb.customCriteriaScores}
                                                                />
                                                              )}
                                                              {fb.generalComments && (
                                                                <div>
                                                                  <p className="text-xs font-medium text-muted-foreground mb-1">
                                                                    Comentários gerais
                                                                  </p>
                                                                  <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-2.5 py-2 whitespace-pre-wrap">
                                                                    {fb.generalComments}
                                                                  </p>
                                                                </div>
                                                              )}
                                                              {fb.employeeId === employee?.id && (
                                                                <Button
                                                                  size="sm"
                                                                  variant="ghost"
                                                                  className="text-xs text-muted-foreground hover:text-foreground h-7 px-2"
                                                                  onClick={() =>
                                                                    openFeedbackDialog(
                                                                      interview,
                                                                      true,
                                                                      fb,
                                                                    )
                                                                  }
                                                                  data-testid={`button-edit-group-feedback-${fb.id}`}
                                                                >
                                                                  <Pencil className="w-3 h-3 mr-1" />
                                                                  Editar avaliação
                                                                </Button>
                                                              )}
                                                            </div>
                                                          ))}
                                                          {interview.feedbackRedacted ? (
                                                            <p
                                                              className="text-xs text-muted-foreground italic"
                                                              data-testid={`text-feedback-redacted-group-${interview.id}`}
                                                            >
                                                              Feedback de outros entrevistadores
                                                              oculto durante fase de entrevistas
                                                            </p>
                                                          ) : (
                                                            interview.interviewerGroupId &&
                                                            interview.groupMemberNames &&
                                                            interview.feedbacks.length <
                                                              interview.groupMemberNames.length &&
                                                            (() => {
                                                              const submittedNames = new Set(
                                                                interview.feedbacks.map(
                                                                  (fb) => fb.employeeName,
                                                                ),
                                                              );
                                                              const pendingNames =
                                                                interview.groupMemberNames!.filter(
                                                                  (name) =>
                                                                    !submittedNames.has(name),
                                                                );
                                                              return (
                                                                <p className="text-xs text-amber-600 dark:text-amber-400 italic">
                                                                  Aguardando feedback de{" "}
                                                                  {pendingNames.join(", ")}.
                                                                </p>
                                                              );
                                                            })()
                                                          )}
                                                        </div>
                                                      ) : interview.completedAt ? (
                                                        <div className="pt-3 border-t border-border/60 space-y-2.5">
                                                          {(interview.gcaVerdict ||
                                                            interview.gcaNotes) && (
                                                            <div>
                                                              <div className="flex items-center justify-between mb-1">
                                                                <p className="text-xs font-medium text-muted-foreground">
                                                                  GCA
                                                                </p>
                                                                {interview.gcaVerdict && (
                                                                  <Badge
                                                                    variant="outline"
                                                                    className="text-[10px] border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400"
                                                                  >
                                                                    {interview.gcaVerdict === "YES"
                                                                      ? "Sim"
                                                                      : interview.gcaVerdict ===
                                                                          "NO"
                                                                        ? "Não"
                                                                        : "N/A"}
                                                                  </Badge>
                                                                )}
                                                              </div>
                                                              {interview.gcaNotes && (
                                                                <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-2.5 py-2 whitespace-pre-wrap">
                                                                  {interview.gcaNotes}
                                                                </p>
                                                              )}
                                                            </div>
                                                          )}
                                                          {(interview.roleKnowledgeVerdict ||
                                                            interview.roleKnowledgeNotes) && (
                                                            <div>
                                                              <div className="flex items-center justify-between mb-1">
                                                                <p className="text-xs font-medium text-muted-foreground">
                                                                  Role-Related Knowledge
                                                                </p>
                                                                {interview.roleKnowledgeVerdict && (
                                                                  <Badge
                                                                    variant="outline"
                                                                    className="text-[10px] border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400"
                                                                  >
                                                                    {interview.roleKnowledgeVerdict ===
                                                                    "YES"
                                                                      ? "Sim"
                                                                      : interview.roleKnowledgeVerdict ===
                                                                          "NO"
                                                                        ? "Não"
                                                                        : "N/A"}
                                                                  </Badge>
                                                                )}
                                                              </div>
                                                              {interview.roleKnowledgeNotes && (
                                                                <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-2.5 py-2 whitespace-pre-wrap">
                                                                  {interview.roleKnowledgeNotes}
                                                                </p>
                                                              )}
                                                            </div>
                                                          )}
                                                          {(interview.compWayVerdict ||
                                                            interview.compWayNotes) && (
                                                            <div>
                                                              <div className="flex items-center justify-between mb-1">
                                                                <p className="text-xs font-medium text-muted-foreground">
                                                                  COMP Way
                                                                </p>
                                                                {interview.compWayVerdict && (
                                                                  <Badge
                                                                    variant="outline"
                                                                    className="text-[10px] border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400"
                                                                  >
                                                                    {interview.compWayVerdict ===
                                                                    "YES"
                                                                      ? "Sim"
                                                                      : interview.compWayVerdict ===
                                                                          "NO"
                                                                        ? "Não"
                                                                        : "N/A"}
                                                                  </Badge>
                                                                )}
                                                              </div>
                                                              {interview.compWayNotes && (
                                                                <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-2.5 py-2 whitespace-pre-wrap">
                                                                  {interview.compWayNotes}
                                                                </p>
                                                              )}
                                                            </div>
                                                          )}
                                                          {!!interview.customCriteriaScores
                                                            ?.length && (
                                                            <CustomCriteriaDisplay
                                                              scores={
                                                                interview.customCriteriaScores
                                                              }
                                                            />
                                                          )}
                                                          {interview.generalComments && (
                                                            <div>
                                                              <p className="text-xs font-medium text-muted-foreground mb-1">
                                                                Comentários gerais
                                                              </p>
                                                              <p className="text-sm text-foreground/80 bg-muted/40 rounded-md px-2.5 py-2 whitespace-pre-wrap">
                                                                {interview.generalComments}
                                                              </p>
                                                            </div>
                                                          )}
                                                          {(interview.interviewerId ===
                                                            employee?.id ||
                                                            interview.filledByEmployeeId ===
                                                              employee?.id) && (
                                                            <Button
                                                              size="sm"
                                                              variant="ghost"
                                                              className="text-xs text-muted-foreground hover:text-foreground h-7 px-2 mt-1"
                                                              onClick={() =>
                                                                openFeedbackDialog(interview, true)
                                                              }
                                                              data-testid={`button-edit-feedback-${interview.id}`}
                                                            >
                                                              <Pencil className="w-3 h-3 mr-1" />
                                                              Editar avaliação
                                                            </Button>
                                                          )}
                                                        </div>
                                                      ) : (
                                                        (() => {
                                                          const canFillFeedback =
                                                            !interview.recommendation &&
                                                            (canManage ||
                                                              interview.interviewer?.id ===
                                                                employee?.id ||
                                                              (employee?.id
                                                                ? (interview.groupMemberIds?.includes(
                                                                    employee.id,
                                                                  ) ?? false)
                                                                : false));
                                                          return (
                                                            <div className="pt-3 border-t border-border/60">
                                                              <p className="text-sm text-muted-foreground mb-2">
                                                                Nenhuma avaliação fornecida ainda.
                                                              </p>
                                                              {canFillFeedback && (
                                                                <Button
                                                                  size="sm"
                                                                  variant="outline"
                                                                  onClick={() =>
                                                                    openFeedbackDialog(interview)
                                                                  }
                                                                  data-testid={`button-fill-feedback-${interview.id}`}
                                                                >
                                                                  <ClipboardEdit className="w-3 h-3 mr-1" />
                                                                  Avaliar Entrevista
                                                                </Button>
                                                              )}
                                                            </div>
                                                          );
                                                        })()
                                                      )}
                                                      <div className="mt-3">
                                                        <CandidateExcitementInterviewBlock
                                                          scores={candidateExcitementSeries}
                                                          interviewId={interview.id}
                                                          highlighted={highlightedExcitementIds.has(
                                                            interview.id,
                                                          )}
                                                        />
                                                      </div>
                                                      {interview.notesForNextInterviewer && (
                                                        <div className="mt-3 pt-3 border-t border-border/60">
                                                          <p className="text-xs font-medium text-muted-foreground mb-1">
                                                            Notas para o próximo entrevistador
                                                          </p>
                                                          <p
                                                            className="text-sm bg-muted/40 border border-border rounded-md px-2.5 py-2 whitespace-pre-wrap"
                                                            data-testid={`text-notes-next-interviewer-${interview.id}`}
                                                          >
                                                            {interview.notesForNextInterviewer}
                                                          </p>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </CollapsibleContent>
                                                </div>
                                              </Collapsible>
                                            ))}
                                            {canManage && (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="text-xs text-muted-foreground hover:text-foreground h-7 px-2"
                                                onClick={() => handleScheduleInterview(stage.id)}
                                                data-testid={`button-schedule-another-${stage.id}`}
                                              >
                                                <Plus className="w-3 h-3 mr-1" />
                                                Agendar outra entrevista
                                              </Button>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-1">
                                            <p
                                              className={`text-sm leading-6 ${
                                                isCurrent
                                                  ? "font-semibold text-foreground"
                                                  : "text-muted-foreground"
                                              }`}
                                            >
                                              {stage.stageName}
                                            </p>
                                            {canManage && (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="text-xs text-muted-foreground hover:text-foreground h-7 w-7 p-0"
                                                onClick={() => handleScheduleInterview(stage.id)}
                                                data-testid={`button-schedule-first-${stage.id}`}
                                                title="Agendar entrevista"
                                              >
                                                <Plus className="w-3 h-3" />
                                              </Button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          ) : details.interviews.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              Nenhuma entrevista realizada ainda.
                            </p>
                          ) : null}
                        </div>
                      )}
                    </div>

                    {/* Comentários */}
                    <div id="section-comentarios" className="border-b border-border/40 px-7">
                      {(() => {
                        const freeNotes = (notes || []).filter((n) => n.type === "free" || !n.type);
                        return (
                          <SectionHeader
                            id="comentarios"
                            icon={<MessageSquare className="w-3.5 h-3.5" />}
                            title="Comentários"
                            count={freeNotes.length}
                            tooltip="Campo livre para anotações gerais sobre o candidato ou processo, sem impacto direto nas avaliações"
                          />
                        );
                      })()}
                      {!collapsedSections.has("comentarios") && (
                        <div className="pb-4">
                          {(() => {
                            const freeNotes = (notes || []).filter(
                              (n) => n.type === "free" || !n.type,
                            );
                            return (
                              freeNotes.length > 0 && (
                                <div className="space-y-2 mb-3">
                                  {[...freeNotes].reverse().map((note) => (
                                    <div
                                      key={note.id}
                                      className="bg-muted/50 rounded-lg p-3"
                                      data-testid={`note-${note.id}`}
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-medium">
                                          {note.author?.fullName || "Autor"}
                                        </span>
                                        <div className="flex items-center gap-1">
                                          <span className="text-xs text-muted-foreground">
                                            {formatDateTime(note.createdAt)}
                                          </span>
                                          {editingNoteId !== note.id && (
                                            <>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={() => {
                                                  setEditingNoteId(note.id);
                                                  setEditingNoteContent(note.content);
                                                }}
                                                data-testid={`button-edit-note-${note.id}`}
                                              >
                                                <Pencil className="w-3 h-3" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 text-destructive hover:text-destructive"
                                                onClick={() => deleteNoteMutation.mutate(note.id)}
                                                disabled={deleteNoteMutation.isPending}
                                                data-testid={`button-delete-note-${note.id}`}
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                      {editingNoteId === note.id ? (
                                        <div className="space-y-2">
                                          <Textarea
                                            value={editingNoteContent}
                                            onChange={(e) => setEditingNoteContent(e.target.value)}
                                            className="text-sm"
                                            rows={2}
                                            data-testid={`input-edit-note-${note.id}`}
                                          />
                                          <div className="flex gap-1 justify-end">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-7 w-7"
                                              onClick={() => {
                                                setEditingNoteId(null);
                                                setEditingNoteContent("");
                                              }}
                                              data-testid={`button-cancel-edit-note-${note.id}`}
                                            >
                                              <X className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button
                                              size="icon"
                                              className="h-7 w-7"
                                              disabled={
                                                !editingNoteContent.trim() ||
                                                updateNoteMutation.isPending
                                              }
                                              onClick={() =>
                                                updateNoteMutation.mutate({
                                                  noteId: note.id,
                                                  content: editingNoteContent.trim(),
                                                })
                                              }
                                              data-testid={`button-save-edit-note-${note.id}`}
                                            >
                                              <Check className="w-3.5 h-3.5" />
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <p className="text-sm whitespace-pre-wrap">
                                            {note.content}
                                          </p>
                                          {renderAttachments(note.attachments)}
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )
                            );
                          })()}
                          <div className="space-y-1">
                            {renderPendingAttachments("freeNote")}
                            <div className="flex gap-2 items-end">
                              <Textarea
                                placeholder="Adicionar uma nota..."
                                value={newNoteContent}
                                onChange={(e) => setNewNoteContent(e.target.value)}
                                onPaste={(e) => handlePaste("freeNote", e)}
                                className="text-sm flex-1 min-w-0"
                                rows={2}
                                data-testid="input-new-note"
                              />
                              {renderAttachButton("freeNote")}
                              <Button
                                type="button"
                                size="icon"
                                className="self-end shrink-0"
                                disabled={
                                  (!newNoteContent.trim() &&
                                    !pendingAttachments.freeNote?.length) ||
                                  createNoteMutation.isPending ||
                                  isUploadingFile
                                }
                                onClick={() =>
                                  createNoteMutation.mutate({
                                    content: newNoteContent.trim() || " ",
                                    attachments: getAttachmentsForKey("freeNote"),
                                  })
                                }
                                data-testid="button-submit-note"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Histórico */}
                    <div id="section-historico" className="px-7">
                      <SectionHeader
                        id="historico"
                        icon={<Clock className="w-3.5 h-3.5" />}
                        title="Histórico"
                        count={
                          details.history.filter((e) => describeHistoryEntry(e).visible).length
                        }
                      />

                      {!collapsedSections.has("historico") && (
                        <div className="pb-4">
                          {details.history.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              Nenhum histórico disponível.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {[...details.history]
                                .reverse()
                                .map((entry) => ({ entry, desc: describeHistoryEntry(entry) }))
                                .filter(({ desc }) => desc.visible)
                                .map(({ entry, desc }) => (
                                  <div
                                    key={entry.id}
                                    className="text-sm py-1"
                                    data-testid={`history-entry-${entry.id}`}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2 min-w-0">
                                        {desc.kind === "move" ? (
                                          desc.oldLabel ? (
                                            <>
                                              <span className="text-muted-foreground">
                                                {desc.oldLabel}
                                              </span>
                                              <span className="text-muted-foreground">→</span>
                                              <span className="font-medium">{desc.newLabel}</span>
                                            </>
                                          ) : (
                                            <span className="font-medium">{desc.newLabel}</span>
                                          )
                                        ) : (
                                          <span className="font-medium">{desc.text}</span>
                                        )}
                                      </div>
                                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                                        {formatDateTime(entry.changedAt)}
                                      </span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                      por {desc.actor}
                                    </div>
                                    {entry.newStatus === "rejected" && details.rejectionReason && (
                                      <div
                                        className="mt-1 ml-0 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs"
                                        data-testid={`rejection-reason-${entry.id}`}
                                      >
                                        <span className="font-medium text-red-700 dark:text-red-300">
                                          Motivo interno:{" "}
                                        </span>
                                        <span className="text-red-600 dark:text-red-400">
                                          {details.rejectionReason}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Right sidebar */}
              <div className="w-[230px] flex-shrink-0 overflow-y-auto border-l border-border/40 bg-muted/30">
                {/* Detalhes section */}
                <div className="px-4 py-3 border-b border-border/40">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Detalhes
                  </h4>
                  <div className="flex flex-col gap-3">
                    <div>
                      <div className="text-[11px] text-muted-foreground">Aplicou em</div>
                      <div className="text-sm" data-testid="text-application-date">
                        {formatDateTime(details.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Triagem por IA section */}
                {(() => {
                  const sr = details?.aiScreeningResult as
                    | {
                        status?: string;
                        verdict?: string;
                        level?: string;
                        gcaDots?: number;
                        compWayDots?: number;
                        evaluationId?: string;
                        error?: string;
                      }
                    | null
                    | undefined;
                  if (!sr) return null;
                  return (
                    <div className="px-4 py-3 border-b border-border/40">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        Triagem por IA
                      </div>
                      {(sr.status === "pending" || sr.status === "running") && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Avaliando perfil...
                        </div>
                      )}
                      {sr.status === "done" && (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                sr.verdict === "interview"
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                              }`}
                            >
                              {sr.verdict === "interview" ? "● ENTREVISTAR" : "● NÃO ENTREVISTAR"}
                            </span>
                            {sr.level && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">
                                {sr.level}
                              </span>
                            )}
                          </div>
                          {(sr.gcaDots !== undefined || sr.compWayDots !== undefined) && (
                            <div className="flex gap-2">
                              {sr.gcaDots !== undefined && (
                                <div className="flex-1 bg-muted/50 rounded p-1.5">
                                  <div className="text-[9px] text-muted-foreground font-semibold uppercase tracking-wide mb-0.5">
                                    GCA
                                  </div>
                                  <div className="text-sm tracking-wider">
                                    {"●".repeat(sr.gcaDots)}
                                    {"○".repeat(5 - sr.gcaDots)}
                                  </div>
                                </div>
                              )}
                              {sr.compWayDots !== undefined && (
                                <div className="flex-1 bg-muted/50 rounded p-1.5">
                                  <div className="text-[9px] text-muted-foreground font-semibold uppercase tracking-wide mb-0.5">
                                    Comp Way
                                  </div>
                                  <div className="text-sm tracking-wider">
                                    {"●".repeat(sr.compWayDots)}
                                    {"○".repeat(5 - sr.compWayDots)}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="flex gap-1.5">
                            {sr.evaluationId && (
                              <a
                                href={`/labs/candidate-intelligence?evaluationId=${sr.evaluationId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 text-center bg-primary text-primary-foreground rounded px-2 py-1 text-xs font-medium hover:bg-primary/90 transition-colors"
                              >
                                Ver avaliação completa →
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => retriggerScreeningMutation.mutate()}
                              disabled={retriggerScreeningMutation.isPending}
                              className="bg-muted border border-border rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                            >
                              {retriggerScreeningMutation.isPending ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                "↺ Reavaliar"
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                      {sr.status === "failed" && (
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-destructive flex items-center gap-1">
                              ⚠ Não foi possível avaliar
                            </span>
                            <button
                              type="button"
                              onClick={() => retriggerScreeningMutation.mutate()}
                              disabled={retriggerScreeningMutation.isPending}
                              className="bg-muted border border-border rounded px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                            >
                              {retriggerScreeningMutation.isPending ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                "↺ Tentar novamente"
                              )}
                            </button>
                          </div>
                          {sr.error && (
                            <p className="text-[11px] text-muted-foreground">{sr.error}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Propriedades section */}
                <div className="px-4 py-3">
                  <button
                    type="button"
                    className="flex items-center justify-between w-full mb-2 group"
                    onClick={() => setPropertiesCollapsed((v) => !v)}
                  >
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide group-hover:text-foreground transition-colors">
                      Propriedades
                    </span>
                    {propertiesCollapsed ? (
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    ) : (
                      <ChevronUp className="h-3 w-3 text-muted-foreground" />
                    )}
                  </button>

                  {!propertiesCollapsed && (
                    <>
                      <div className="flex flex-col gap-2">
                        {propertyDefinitions.map((def) => {
                          const val = propertyValues.find((v) => v.definitionId === def.id);
                          return (
                            <div key={def.id} className="group/prop">
                              {/* Property name */}
                              {editingDefId === def.id ? (
                                <Input
                                  defaultValue={def.name}
                                  autoFocus
                                  className="h-6 text-xs mb-1"
                                  onBlur={(e) => {
                                    if (e.target.value.trim()) {
                                      updateDefMutation.mutate({
                                        id: def.id,
                                        data: { name: e.target.value.trim() },
                                      });
                                    }
                                    setEditingDefId(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      if (e.currentTarget.value.trim()) {
                                        updateDefMutation.mutate({
                                          id: def.id,
                                          data: { name: e.currentTarget.value.trim() },
                                        });
                                      }
                                      setEditingDefId(null);
                                    }
                                    if (e.key === "Escape") setEditingDefId(null);
                                  }}
                                />
                              ) : (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="text-[11px] text-muted-foreground hover:text-foreground truncate block w-full text-left"
                                      title={def.name}
                                    >
                                      {def.name}
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="start" className="w-48">
                                    <DropdownMenuItem onSelect={() => setEditingDefId(def.id)}>
                                      <Pencil className="h-3.5 w-3.5 mr-2" />
                                      Renomear
                                    </DropdownMenuItem>
                                    <DropdownMenuSub>
                                      <DropdownMenuSubTrigger>
                                        <FileText className="h-3.5 w-3.5 mr-2" />
                                        Tipo
                                      </DropdownMenuSubTrigger>
                                      <DropdownMenuPortal>
                                        <DropdownMenuSubContent>
                                          <DropdownMenuItem
                                            onSelect={() =>
                                              updateDefMutation.mutate({
                                                id: def.id,
                                                data: { type: "text" },
                                              })
                                            }
                                            className={def.type === "text" ? "font-medium" : ""}
                                          >
                                            {def.type === "text" && (
                                              <Check className="h-3.5 w-3.5 mr-2" />
                                            )}
                                            {def.type !== "text" && <span className="w-3.5 mr-2" />}
                                            Texto
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onSelect={() =>
                                              updateDefMutation.mutate({
                                                id: def.id,
                                                data: { type: "select" },
                                              })
                                            }
                                            className={def.type === "select" ? "font-medium" : ""}
                                          >
                                            {def.type === "select" && (
                                              <Check className="h-3.5 w-3.5 mr-2" />
                                            )}
                                            {def.type !== "select" && (
                                              <span className="w-3.5 mr-2" />
                                            )}
                                            Seleção
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onSelect={() =>
                                              updateDefMutation.mutate({
                                                id: def.id,
                                                data: { type: "multi_select" },
                                              })
                                            }
                                            className={
                                              def.type === "multi_select" ? "font-medium" : ""
                                            }
                                          >
                                            {def.type === "multi_select" && (
                                              <Check className="h-3.5 w-3.5 mr-2" />
                                            )}
                                            {def.type !== "multi_select" && (
                                              <span className="w-3.5 mr-2" />
                                            )}
                                            Multi-seleção
                                          </DropdownMenuItem>
                                        </DropdownMenuSubContent>
                                      </DropdownMenuPortal>
                                    </DropdownMenuSub>
                                    <DropdownMenuItem
                                      onSelect={() =>
                                        updateDefMutation.mutate({
                                          id: def.id,
                                          data: { showOnCard: !def.showOnCard },
                                        })
                                      }
                                    >
                                      <Eye className="h-3.5 w-3.5 mr-2" />
                                      {def.showOnCard ? "Ocultar no card" : "Mostrar no card"}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onSelect={() => setDeleteDefId(def.id)}
                                    >
                                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                                      Excluir
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                              {/* Value */}
                              <div className="mt-0.5">
                                <PropertyValueCell
                                  definition={def}
                                  value={val}
                                  applicationId={applicationId!}
                                  jobQueryKey={
                                    details?.jobOpening?.id
                                      ? (orpc.applications.getJobKanban.queryOptions({
                                          input: { jobOpeningId: details.jobOpening.id },
                                        }).queryKey as unknown as string[])
                                      : []
                                  }
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        type="button"
                        className="mt-3 text-xs text-primary flex items-center gap-1 hover:underline"
                        title="Essa propriedade será adicionada a todos os candidatos deste processo"
                        onClick={async () => {
                          if (!details?.jobOpening?.id) return;
                          try {
                            await rpcClient.jobOpenings.createPropertyDefinition({
                              id: details.jobOpening.id,
                            });
                          } catch {
                            toast({ title: "Erro ao criar propriedade", variant: "destructive" });
                            return;
                          }
                          queryClient.invalidateQueries({
                            queryKey: orpc.applications.getJobKanban.queryOptions({
                              input: { jobOpeningId: details.jobOpening.id },
                            }).queryKey,
                          });
                        }}
                      >
                        <Plus className="h-3 w-3" />
                        Adicionar propriedade
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {details && applicationId && (
          <>
            <EditApplicantDialog
              open={isEditOpen}
              onOpenChange={setIsEditOpen}
              applicationId={applicationId}
              initialData={{
                applicantName: details.applicant?.fullName || "",
                applicantEmail: details.applicant?.email || "",
                applicantPhone: details.applicant?.phone || "",
                linkedinUrl: details.applicant?.linkedinUrl || "",
                type: details.type || "",
                coverLetter: details.coverLetter || "",
              }}
            />
            <CreateOfferDialog
              applicationId={applicationId}
              applicantName={details.applicant?.fullName || ""}
              jobTitle={details.jobOpening?.title || ""}
              defaultJobLevel={details.jobOpening?.jobLevel}
              open={isCreateOfferOpen}
              onOpenChange={setIsCreateOfferOpen}
            />
            <CreateOfferDialog
              applicationId={applicationId}
              applicantName={details.applicant?.fullName || ""}
              jobTitle={details.jobOpening?.title || ""}
              defaultJobLevel={details.jobOpening?.jobLevel}
              open={isSimulateOfferOpen}
              onOpenChange={setIsSimulateOfferOpen}
              mode="simulate"
            />

            {details.status === "rejected" && (
              <SendRejectionEmailDialog
                open={isRejectionEmailDialogOpen}
                onOpenChange={setIsRejectionEmailDialogOpen}
                applicationId={applicationId}
                candidateName={details.applicant?.fullName || "Candidato"}
                jobId={details.jobOpening?.id}
                hiringManagerName={(details.jobOpening as any)?.hiringManager?.fullName}
              />
            )}

            {sendEmailInterviewId &&
              (() => {
                const interview = details.interviews?.find((i) => i.id === sendEmailInterviewId);
                if (!interview) return null;
                const stage = details.interviewStages?.find(
                  (s) => s.id === interview.interviewStageId,
                );
                const interviewerName =
                  interview.groupMemberNames?.join(", ") ||
                  interview.interviewer?.fullName ||
                  "Entrevistador";
                const interviewerEmail =
                  (interview as any).interviewerGroup?.corporateEmail ||
                  (interview.interviewer as any)?.corporateEmail ||
                  "";

                return (
                  <SendInterviewEmailDialog
                    open={!!sendEmailInterviewId}
                    onOpenChange={(o) => {
                      if (!o) setSendEmailInterviewId(null);
                    }}
                    interviewId={sendEmailInterviewId}
                    applicationId={applicationId}
                    jobId={details.jobOpening?.id}
                    jobTitle={details.jobOpening?.title || "a vaga"}
                    stageName={stage?.stageName || "Entrevista"}
                    stageDescription={stage?.description || undefined}
                    interviewerName={interviewerName}
                    interviewerEmail={interviewerEmail}
                    durationMinutes={stage?.durationMinutes || 60}
                    candidateName={details.applicant?.fullName}
                  />
                );
              })()}

            {(() => {
              const interview = details?.interviews.find((i) => i.id === changeInterviewerForId);
              if (!interview || !changeInterviewerForId) return null;
              const changeStage = details?.interviewStages?.find(
                (s) => s.id === interview.interviewStageId,
              );
              return (
                <ChangeInterviewerDialog
                  open={!!changeInterviewerForId}
                  onOpenChange={(open) => {
                    if (!open) setChangeInterviewerForId(null);
                  }}
                  interviewId={changeInterviewerForId}
                  interviewStageId={interview.interviewStageId}
                  applicationId={applicationId!}
                  jobOpeningId={details!.jobOpening!.id}
                  hasCalendarEvent={!!interview.googleCalendarEventId}
                  currentInterviewerId={interview.interviewerId ?? ""}
                  currentInterviewerGroupId={interview.interviewerGroupId}
                  durationMinutes={changeStage?.durationMinutes || 60}
                />
              );
            })()}

            <Dialog
              open={!!deleteInterviewId}
              onOpenChange={(open) => {
                if (!open) {
                  setDeleteInterviewId(null);
                  setDeleteConfirmCode("");
                  setDeleteExpectedCode("");
                }
              }}
            >
              <DialogContent data-testid="dialog-confirm-delete-interview">
                <DialogHeader>
                  <DialogTitle>Excluir entrevista</DialogTitle>
                </DialogHeader>
                {deleteInterviewTarget &&
                  (() => {
                    const isFinished = deleteInterviewTarget.status === "finished";
                    const hasGroupFeedbacks =
                      deleteInterviewTarget.feedbacks && deleteInterviewTarget.feedbacks.length > 0;
                    const hasIndividualFeedback =
                      deleteInterviewTarget.completedAt ||
                      deleteInterviewTarget.recommendation ||
                      !!deleteInterviewTarget.gcaVerdict ||
                      !!deleteInterviewTarget.roleKnowledgeVerdict ||
                      !!deleteInterviewTarget.compWayVerdict;
                    const hasFeedback = hasGroupFeedbacks || hasIndividualFeedback;
                    if (!isFinished && !hasFeedback) return null;
                    return (
                      <div
                        className="flex items-start gap-2 rounded-md border border-[hsl(var(--amber-state))]/30 bg-[hsl(var(--amber-mist))] p-3"
                        data-testid="alert-delete-interview-warning"
                      >
                        <AlertTriangle className="w-5 h-5 text-[hsl(var(--amber-state))] shrink-0 mt-0.5" />
                        <div className="text-sm text-[hsl(var(--ink-primary))]">
                          {isFinished && hasFeedback
                            ? "Esta entrevista já foi finalizada e possui feedbacks registrados. Todos os dados serão perdidos permanentemente."
                            : isFinished
                              ? "Esta entrevista já foi finalizada. Todos os dados serão perdidos permanentemente."
                              : "Esta entrevista possui feedbacks registrados. Todos os dados serão perdidos permanentemente."}
                        </div>
                      </div>
                    );
                  })()}
                <p className="text-sm text-muted-foreground">
                  Tem certeza que deseja excluir esta entrevista? Esta ação é irreversível. Os
                  feedbacks e registros de histórico associados também serão removidos.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="delete-interview-code" className="text-sm">
                    Digite o código{" "}
                    <span className="font-mono font-bold text-foreground tracking-widest">
                      {deleteExpectedCode}
                    </span>{" "}
                    para confirmar:
                  </Label>
                  <Input
                    id="delete-interview-code"
                    data-testid="input-delete-interview-code"
                    value={deleteConfirmCode}
                    onChange={(e) => setDeleteConfirmCode(e.target.value.toUpperCase())}
                    placeholder="Digite o código"
                    className="font-mono tracking-widest"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    data-testid="button-cancel-delete-interview"
                    onClick={() => {
                      setDeleteInterviewId(null);
                      setDeleteConfirmCode("");
                      setDeleteExpectedCode("");
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    data-testid="button-confirm-delete-interview"
                    disabled={
                      deleteConfirmCode !== deleteExpectedCode || deleteInterviewMutation.isPending
                    }
                    onClick={() => deleteInterviewMutation.mutate()}
                  >
                    {deleteInterviewMutation.isPending ? "Excluindo..." : "Excluir"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <RescheduleConfirmDialog
              open={!!reschedule.rescheduleInterviewId}
              onOpenChange={(open) => {
                if (!open) reschedule.closeReschedule();
              }}
              isPending={reschedule.isPending}
              isGroupInterview={reschedule.isGroupInterview}
              onConfirm={reschedule.confirmReschedule}
              onCancel={reschedule.closeReschedule}
            />

            {scheduleAnotherStageId &&
              (() => {
                const scheduleStage = details?.interviewStages?.find(
                  (s) => s.id === scheduleAnotherStageId,
                );
                return (
                  <ScheduleInterviewDialog
                    open={!!scheduleAnotherStageId}
                    onOpenChange={(open) => {
                      if (!open) setScheduleAnotherStageId(null);
                    }}
                    applicationId={applicationId || ""}
                    stageId={scheduleAnotherStageId}
                    stageName={scheduleStage?.stageName || ""}
                    candidateName={details?.applicant?.fullName || "Candidato"}
                    jobOpeningId={details?.jobOpening?.id || ""}
                    durationMinutes={scheduleStage?.durationMinutes}
                    stageDescription={scheduleStage?.description || ""}
                    jobTitle={details?.jobOpening?.title || ""}
                    applicationStatus={details?.status}
                    currentInterviewStageId={details?.currentInterviewStageId ?? null}
                  />
                );
              })()}

            <AlertDialog
              open={!!deleteOfferId}
              onOpenChange={(open) => {
                if (!open) setDeleteOfferId(null);
              }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir oferta</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir esta oferta? Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => {
                      if (deleteOfferId) deleteOfferMutation.mutate(deleteOfferId);
                      setDeleteOfferId(null);
                    }}
                  >
                    Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Dialog
              open={!!moveInterviewId}
              onOpenChange={(open) => {
                if (!open) {
                  setMoveInterviewId(null);
                  setMoveTargetStageId("");
                }
              }}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Mover Entrevista</DialogTitle>
                  <DialogDescription>Selecione a etapa destino.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  {details.interviewStages
                    .filter(
                      (s) =>
                        !s.hidden &&
                        !s.archivedAt &&
                        s.id !== currentMoveInterview?.interviewStageId,
                    )
                    .sort((a, b) => a.stageOrder - b.stageOrder)
                    .map((s) => (
                      <label
                        key={s.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border cursor-pointer",
                          moveTargetStageId === s.id && "border-primary bg-primary/5",
                        )}
                      >
                        <input
                          type="radio"
                          checked={moveTargetStageId === s.id}
                          onChange={() => setMoveTargetStageId(s.id)}
                        />
                        <div>
                          <div className="font-medium">{s.stageName}</div>
                          <div className="text-sm text-muted-foreground">
                            {s.durationMinutes} min
                          </div>
                        </div>
                      </label>
                    ))}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMoveInterviewId(null)}>
                    Cancelar
                  </Button>
                  <Button
                    disabled={!moveTargetStageId || moveInterviewMutation.isPending}
                    onClick={() => {
                      if (moveInterviewId && moveTargetStageId) {
                        moveInterviewMutation.mutate({
                          interviewId: moveInterviewId,
                          interviewStageId: moveTargetStageId,
                        });
                      }
                    }}
                  >
                    Mover Entrevista
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </DialogContent>

      <InterviewFeedbackDialog
        open={feedbackDialogOpen}
        onOpenChange={(open) => {
          setFeedbackDialogOpen(open);
          if (!open) setIsEditingFeedback(false);
        }}
        interview={feedbackDialogInterview}
        isEditing={isEditingFeedback}
      />

      <MoveConfirmationDialog
        moveState={moveApp}
        people={movePeople}
        stageInterviewers={moveStageInterviewers}
        isLoadingStageInterviewers={isLoadingMoveStageInterviewers}
        jobId={jobId}
        jobTitle={details?.jobOpening?.title || ""}
        jobLevel={details?.jobOpening?.jobLevel}
      />

      {/* Reference Check View Dialog */}
      <Dialog open={!!viewingRc} onOpenChange={(open) => !open && setViewingRc(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>{viewingRc?.referrerName}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-1 pb-2 flex-1 space-y-5">
            {viewingRc && (viewingRc.answers?.length ?? 0) > 0 ? (
              viewingRc
                .answers!.filter((a) => a.answer.trim().length > 0)
                .map((item) => (
                  <div key={item.question} className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {item.question}
                    </p>
                    <p className="text-sm">{item.answer}</p>
                  </div>
                ))
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma resposta registrada.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reference Check Template Dialog */}
      <Dialog open={rcTemplateOpen} onOpenChange={setRcTemplateOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Template de Reference Check</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-1 pb-2 flex-1 space-y-4">
            {DEFAULT_REFERENCE_QUESTIONS.map((q, idx) => (
              <div key={q} className="space-y-0.5">
                <p className="text-xs font-medium text-muted-foreground">{idx + 1}.</p>
                <p className="text-sm">{q}</p>
              </div>
            ))}
          </div>
          <div className="pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                const text = DEFAULT_REFERENCE_QUESTIONS.map((q, i) => `${i + 1}. ${q}`).join(
                  "\n\n",
                );
                navigator.clipboard.writeText(text);
                toast({ title: "Template copiado!" });
              }}
            >
              <Copy className="w-3 h-3 mr-1" />
              Copiar template
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reference Check Dialog */}
      <Dialog
        open={rcDrawerOpen}
        onOpenChange={(open) => {
          setRcDrawerOpen(open);
          if (!open) {
            setEditingRc(null);
            setRcPasteOpen(false);
            setRcPasteText("");
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {editingRc ? "Editar Reference Check" : "Adicionar Reference Check"}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-1 pb-2 flex-1">
            <div className="flex border-b mb-4">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium transition-colors ${rcDrawerTab === "contato" ? "border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                onClick={() => setRcDrawerTab("contato")}
              >
                Contato
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium transition-colors ${rcDrawerTab === "respostas" ? "border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                onClick={() => setRcDrawerTab("respostas")}
              >
                Respostas
              </button>
            </div>

            {rcDrawerTab === "contato" && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const contactData = {
                    referrerName: rcContactForm.referrerName,
                    referrerLinkedin: rcContactForm.referrerLinkedin || null,
                    referrerPhone: rcContactForm.referrerPhone || null,
                  };
                  if (editingRc) {
                    updateRcMutation.mutate({ id: editingRc.id, applicationId, ...contactData });
                  } else {
                    createRcMutation.mutate({
                      applicationId,
                      ...contactData,
                      answers: rcAnswersForm,
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                    e.currentTarget.requestSubmit();
                }}
                className="space-y-4"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="rc-name">Nome *</Label>
                  <Input
                    id="rc-name"
                    value={rcContactForm.referrerName}
                    onChange={(e) =>
                      setRcContactForm((prev) => ({ ...prev, referrerName: e.target.value }))
                    }
                    required
                    placeholder="Nome completo"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rc-linkedin">LinkedIn (opcional)</Label>
                  <Input
                    id="rc-linkedin"
                    value={rcContactForm.referrerLinkedin}
                    onChange={(e) =>
                      setRcContactForm((prev) => ({ ...prev, referrerLinkedin: e.target.value }))
                    }
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rc-phone">Telefone (opcional)</Label>
                  <Input
                    id="rc-phone"
                    value={rcContactForm.referrerPhone}
                    onChange={(e) =>
                      setRcContactForm((prev) => ({ ...prev, referrerPhone: e.target.value }))
                    }
                    placeholder="+55 11 99999-9999"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={createRcMutation.isPending || updateRcMutation.isPending}
                  className="w-full"
                >
                  {createRcMutation.isPending || updateRcMutation.isPending
                    ? "Salvando..."
                    : "Salvar"}
                </Button>
              </form>
            )}

            {rcDrawerTab === "respostas" && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (editingRc) {
                    updateRcMutation.mutate({
                      id: editingRc.id,
                      applicationId,
                      answers: rcAnswersForm,
                    });
                  } else {
                    createRcMutation.mutate({
                      applicationId,
                      referrerName: rcContactForm.referrerName,
                      referrerLinkedin: rcContactForm.referrerLinkedin || null,
                      referrerPhone: rcContactForm.referrerPhone || null,
                      answers: rcAnswersForm,
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                    e.currentTarget.requestSubmit();
                }}
                className="space-y-6"
              >
                {/* Paste area */}
                <div className="rounded-md border border-dashed border-border bg-muted/30 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setRcPasteOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  >
                    <span className="flex items-center gap-1.5">
                      <ClipboardEdit className="w-3.5 h-3.5" />
                      Colar respostas em bloco
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${rcPasteOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {rcPasteOpen && (
                    <div className="px-3 pb-3 space-y-2">
                      <Textarea
                        value={rcPasteText}
                        onChange={(e) => setRcPasteText(e.target.value)}
                        rows={6}
                        placeholder={
                          "Cole aqui as respostas. Formatos suportados:\n\n1. Resposta da primeira pergunta\n2. Resposta da segunda...\n\nou a resposta abaixo de cada pergunta."
                        }
                        className="text-sm resize-none"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="w-full"
                        disabled={rcPasteText.trim().length === 0}
                        onClick={() => {
                          const parsed = parseReferenceCheckResponses(
                            rcPasteText,
                            DEFAULT_REFERENCE_QUESTIONS,
                          );
                          const extras = rcAnswersForm.filter(
                            (a) => !DEFAULT_REFERENCE_QUESTIONS.includes(a.question),
                          );
                          setRcAnswersForm([...parsed, ...extras]);
                          const filled = parsed.filter((p) => p.answer.trim().length > 0).length;
                          toast({
                            title: `${filled} resposta${filled !== 1 ? "s" : ""} identificada${filled !== 1 ? "s" : ""}`,
                          });
                          setRcPasteOpen(false);
                          setRcPasteText("");
                        }}
                      >
                        Interpretar
                      </Button>
                    </div>
                  )}
                </div>

                {rcAnswersForm.map((item, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: user can add duplicate empty questions via "Adicionar pergunta"
                  <div key={idx} className="space-y-1.5">
                    {DEFAULT_REFERENCE_QUESTIONS.includes(item.question) ? (
                      <Label className="text-sm leading-snug">{item.question}</Label>
                    ) : (
                      <Input
                        value={item.question}
                        onChange={(e) => {
                          const updated = [...rcAnswersForm];
                          updated[idx] = { ...updated[idx], question: e.target.value };
                          setRcAnswersForm(updated);
                        }}
                        placeholder="Pergunta..."
                        className="text-sm"
                      />
                    )}
                    <Textarea
                      value={item.answer}
                      onChange={(e) => {
                        const updated = [...rcAnswersForm];
                        updated[idx] = { ...updated[idx], answer: e.target.value };
                        setRcAnswersForm(updated);
                      }}
                      rows={3}
                      placeholder="Resposta..."
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setRcAnswersForm((prev) => [...prev, { question: "", answer: "" }])
                  }
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Adicionar pergunta
                </Button>
                <Button type="submit" disabled={updateRcMutation.isPending} className="w-full mt-2">
                  {updateRcMutation.isPending ? "Salvando..." : "Salvar respostas"}
                </Button>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
